import * as acequia from './acequia.js'
import { createErrorResponse } from './errors.js'
import { Evented } from './evented.js'
import { Router } from './routes.js'
import { createPeer, getIceServers, invalidateIceCache, _publishNewPeer } from './webrtc/webrtc.js'
import { DEFAULT_TRANSPORT, resolveTransport } from './webrtc/transport.js'
import { normalizeBody } from './requestNormalize.js'
import { normalizeResponse } from './responseNormalize.js'
import { shouldDeposeLeader } from './leaderDepose.js'
import { signHello } from './hello.js'
import { heartbeatDelay } from './heartbeat.js'
import { ConnectBackoff } from './connectBackoff.js'
import { peerLimitActive, PEER_LIMIT_CODE } from './webrtc/peerLimit.js'

const { debug } = acequia.config

/**
 * An SDP's session id (its o= line). It is unique per RTCPeerConnection and
 * kept across that connection's renegotiations, so an offer with a different
 * one comes from a new connection: the remote page reloaded (it keeps its
 * instance id), or it gave up on the old connection and made another.
 */
export function sdpSessionId(sdp) {
    const m = /^o=\S+ (\S+) /m.exec(sdp || '')
    return m ? m[1] : null
}

// ---------------------------------------------------------------------------
// Topic Matching (Event Bus)
// ---------------------------------------------------------------------------

/**
 * Match a topic against a subscription pattern.
 * Segments are colon-delimited.  '*' matches one segment, '**' matches rest.
 *
 *   topicMatches('file:changed', 'file:changed')  → true   (exact)
 *   topicMatches('file:*',       'file:changed')  → true   (* = one segment)
 *   topicMatches('file:**',      'file:a:b:c')    → true   (** = any depth)
 *   topicMatches('*:changed',    'file:changed')  → true
 *   topicMatches('**',           'anything')       → true
 *   topicMatches('a:b',          'a:b:c')          → false  (segment count)
 */
export function topicMatches(pattern, topic) {
    if (pattern === topic) return true
    if (pattern === '**') return true

    const patParts = pattern.split(':')
    const topParts = topic.split(':')

    let pi = 0, ti = 0
    while (pi < patParts.length && ti < topParts.length) {
        if (patParts[pi] === '**') return true
        if (patParts[pi] !== '*' && patParts[pi] !== topParts[ti]) return false
        pi++
        ti++
    }

    return pi === patParts.length && ti === topParts.length
}

export async function amIAMember(discoveryURL, groupId) {
    let deviceId = acequia.getDeviceId()
    try {
        let result = await (
            await fetch(`${discoveryURL}/groups/${groupId}/members/${deviceId}`)
        ).json()
        return result
    } catch (e) {
        console.log('am i a member: ', e)
    }
    return false
}

export class Group extends Evented {
    constructor(groupId, config = {}) {
        super()

        this.router = new Router(this)
        this.peersList = {} // Store peers list from discovery server
        this.pendingPeerRequests = {} // Track pending WebRTC peer requests
        this._peers = {} // Per-group WebRTC peer connections, keyed by peerId

        const defaultConfig = {
            discoveryURL: acequia.getDiscoveryOrigin(),
            onClose: () => {},
            onMessage: () => {},
            onPeersList: () => {},
            onError: (event, err) => {
                console.warn('onError: ', err, event)
            },
            serverProxiedRequestHandler: () => {},
            capabilities: [],
        }

        this.config = { ...defaultConfig, ...config }
        // Data-channel transport tuning for every peer this group creates
        // (water marks, timeouts, stream gate, ICE policy) — see
        // webrtc/transport.js. Validated here so a bad override fails at
        // construction rather than at the first large transfer.
        this.transport = resolveTransport(config.transport)
        this._extraDeviceInfo = {}

        // Shared state (peer-elected leader pattern)
        this._state = {}
        this._stateVersion = 0
        this._stateLeader = null
        this._isStateLeader = false
        this._wsRelayPeers = new Set()
        this._iceRetryState = new Map()  // peerId → { count, lastAttempt }
        this._connectBackoff = new ConnectBackoff() // opportunistic connects after a failure (connectBackoff.js)
        this._knownPeerIds = new Set()   // peers seen in the prior peersList event (for new-peer diffing)
        this._connectingPeers = new Set()
        this._wsRelayPromotionTimer = null
        this._wsReconnectAttempt = 0
        this._wsReconnectStableTimer = null
        this._electionEpoch = 0
        this._acceptingPatches = true
        this._patchQueue = []
        this._leaderPendingPatches = []  // patches queued before leader is set
        this._stateCollector = null
        this._stateSentPeers = new Set()  // peers that have received full state
        this._fallbackRecoveryUntil = 0   // timestamp: accept & merge groupState:response until this time
        this._lastActiveTimestamp = Date.now()
        this._lastLeaderResponse = 0     // timestamp: last message received from the elected leader
        this._leaderHealthId = null       // interval timer for leader health checks
        this._leaderSince = 0             // timestamp: when this page applied the current leader
        this._unansweredResyncs = 0       // resync requests the health monitor sent since the leader last answered
        this._lastDeposeAt = 0            // timestamp: when this page last deposed a silent leader
        this._zombieCheckTimer = null     // periodic data channel liveness sweep
        this._routeHealthTimer = null    // periodic route drift check
        this._serverCapabilityList = null // null = no server support yet; [] = server-authoritative list
        this._lastLoggedStateSnapshot = null // for change-only logging

        // Wake detection — request full state resync + route re-registration after sleep
        this._visibilityHandler = () => {
            if (document.visibilityState !== 'visible') return
            const gap = Date.now() - this._lastActiveTimestamp
            if (gap < 30_000) return
            console.log(`[state] wake detected after ${Math.round(gap / 1000)}s gap — requesting resync`)

            // Re-register instance with SW (SW may have been terminated during sleep)
            acequia.postToSW({ type: 'REGISTER_INSTANCE', instanceId: acequia.getInstanceId() })

            // Re-register routes via WS so server broadcasts fresh registry to SW
            if (this.server?.readyState === WebSocket.OPEN) {
                const localRoutes = this.router.getRouteRegistry()
                const routeEntries = Object.entries(localRoutes)
                if (routeEntries.length > 0) {
                    console.log(`[state] wake: re-registering ${routeEntries.length} routes`)
                    this.server.send(JSON.stringify({
                        type: 'registerRoutes',
                        routes: routeEntries.map(([pattern, entry]) => ({
                            pattern, metadata: entry.metadata || {},
                        })),
                        instanceId: this.getDeviceInfo().instanceId,
                    }))
                }
            }

            if (this._isStateLeader) {
                // Leader: re-collect state from peers
                const peers = Object.keys(this.peersList)
                    .filter(id => id !== acequia.getInstanceId())
                const requestMsg = { type: 'groupState:request' }
                for (const peerId of peers) {
                    this._stateSendToPeer(peerId, requestMsg)
                }
            } else if (this._stateLeader) {
                // Follower: ask leader for full state
                this._stateSendToPeer(this._stateLeader, {
                    type: 'groupState:resyncRequest',
                })
                // If state is empty, also broadcast to all peers (leader may be unresponsive)
                if (Object.keys(this._state).length === 0) {
                    const myId = acequia.getInstanceId()
                    const peers = Object.keys(this.peersList).filter(id => id !== myId)
                    console.log(`[state] wake: also requesting from all ${peers.length} peers (empty state)`)
                    this._fallbackRecoveryUntil = Date.now() + 10000
                    for (const peerId of peers) {
                        this._stateSendToPeer(peerId, { type: 'groupState:request' })
                    }
                }
            }
        }
        // Probe connection on network change (Wi-Fi ↔ cellular handoff)
        this._onlineHandler = () => {
            console.log('[lifecycle] online event — probing connection')
            if (this.server?.readyState === WebSocket.OPEN) {
                // Connection looks open but may be stale — send a probe heartbeat
                try {
                    this.server.send(JSON.stringify({ type: 'heartbeat', deviceInfo: this.getDeviceInfo() }))
                } catch (e) {
                    console.warn('[lifecycle] probe send failed, reconnecting:', e.message)
                    this.setupServer()
                }
            } else if (!this._destroyed) {
                // WS is closed/closing — reconnect immediately
                this._wsReconnectAttempt = 0
                this.setupServer()
            }
        }
        // Resume from OS-level freeze (iOS Safari tab freeze, bfcache)
        this._resumeHandler = () => {
            console.log('[lifecycle] resume/pageshow event — revalidating connection')
            // Treat as wake — run the same recovery as visibilitychange
            this._lastActiveTimestamp = 0  // force gap detection
            this._visibilityHandler()
        }
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this._visibilityHandler)
        }
        if (typeof window !== 'undefined') {
            window.addEventListener('online', this._onlineHandler)
            window.addEventListener('pageshow', (e) => {
                if (e.persisted) this._resumeHandler()
            })
            document.addEventListener('resume', this._resumeHandler)
        }

        // Event bus (decentralized pub/sub)
        this._localSubscriptions = new Map()    // pattern → Set<callback>
        this._remoteSubscriptions = new Map()   // peerId → Set<pattern>
        this._retainedEvents = new Map()        // topic → event object
        this._seenEvents = new Set()            // msgId dedup
        this._seenEventsQueue = []              // LRU eviction order
        this._ebWsRelayPeers = new Set()        // peers where WebRTC connect failed
        this._retainedTimers = new Map()        // topic → timeoutId for TTL cleanup

        this._destroyed = false

        // Auto-respond to group:ping messages
        this.on('peerMessage', ({ peerId, message, transport }) => {
            if (message?.type === 'group:ping') {
                const pong = {
                    type: 'group:pong',
                    pingId: message.pingId,
                    timestamp: Date.now(),
                    inboundTransport: transport,
                }
                if (message.requestStatus) {
                    const groupPeers = this._peers
                    const connectedPeers = Object.keys(groupPeers).filter(
                        id => groupPeers[id] && !groupPeers[id]._destroyed &&
                              groupPeers[id]._channel?.readyState === 'open'
                    )
                    pong.status = {
                        connectedPeers,
                        uptime: Date.now() - (this._createdAt || Date.now()),
                    }
                }
                this.sendToPeer(peerId, pong)
            }
        })

        this._createdAt = Date.now()

        // Web storage exists only in a window; a Node process (a registered
        // BrowserDAV serving a directory, 2026-09-28) names itself in config.
        const stored = (store) => (typeof store !== 'undefined' ? store.getItem('displayName') : null)
        this.displayName =
            this.config.displayName ||
            acequia.getHandle?.() ||
            stored(typeof sessionStorage !== 'undefined' ? sessionStorage : undefined) ||
            stored(typeof localStorage !== 'undefined' ? localStorage : undefined) ||
            'anonymous'

        let resolveReady
        this.ready = new Promise((_resolve) => {
            resolveReady = _resolve
        })
        let resolver = () => {
            resolveReady(true)
            this.off('welcome', resolver)
        }
        this.on('welcome', resolver)

        this.connected = false

        if (this.config.onOpen) this.on('welcome', this.config.onOpen)
        if (this.config.onClose) this.on('close', this.config.onClose)
        if (this.config.onMessage) this.on('message', this.config.onMessage)
        if (this.config.onPeersList)
            this.on('peersList', this.config.onPeersList)
        if (this.config.onError) this.on('error', this.config.onError)

        // State leader election — server-authoritative via capabilityList,
        // with peersList-based fallback for cleanup when peers disappear
        this.on('peersList', () => this._onPeersListForLeader())

        // Handle state + event bus messages arriving via WS relay
        this.on('peerMessage', ({ peerId, message }) => {
            // Shared state
            if (message.type === 'groupState:patch' && this._isStateLeader) {
                this._handleStatePatch(message.patch, peerId)
            } else if (message.type === 'groupState:changed') {
                this._handleStateChanged(message)
            } else if (message.type === 'groupState:request') {
                this._handleStateRequest(peerId)
            } else if (message.type === 'groupState:response') {
                if (this._stateCollector) {
                    this._stateCollector(message)
                } else if (!this._isStateLeader && this._fallbackRecoveryUntil > Date.now() && message.state) {
                    // Merge state from peer — multiple peers each contribute their local view
                    this._deepMerge(this._state, message.state)
                    if (message.version > this._stateVersion) this._stateVersion = message.version
                    console.log(`[state] merged fallback state response: now ${Object.keys(this._state).length} keys`)
                    this.fire('stateChanged', {
                        state: this._state,
                        patch: null,
                        peerId: null,
                        version: this._stateVersion,
                    })
                }
            } else if (message.type === 'groupState:resyncRequest' && this._isStateLeader) {
                this._handleResyncRequest(peerId)
            }
            // Event bus
            else if (message.type === 'eventBus:event') {
                this._handleIncomingEvent(message)
            } else if (message.type === 'eventBus:sub') {
                this._handleRemoteSub(peerId, message.topics)
            } else if (message.type === 'eventBus:unsub') {
                this._handleRemoteUnsub(peerId, message.topics)
            } else if (message.type === 'eventBus:syncSubs') {
                this._handleRemoteSync(peerId, message.topics)
            }
        })

        // Event bus: clean up departed peers and retry connections on peersList changes
        this.on('peersList', () => {
            // Remove subscriptions for peers no longer in the group
            for (const peerId of this._remoteSubscriptions.keys()) {
                if (!this.peersList[peerId]) {
                    this._remoteSubscriptions.delete(peerId)
                }
            }
            // Remove departed peers from relay set and state-sent tracking
            for (const peerId of [...this._ebWsRelayPeers]) {
                if (!this.peersList[peerId]) this._ebWsRelayPeers.delete(peerId)
            }
            for (const peerId of [...this._stateSentPeers]) {
                if (!this.peersList[peerId]) this._stateSentPeers.delete(peerId)
            }
            this._ebWsRelayPeers.clear()
            // Clear ICE retry state and WS relay flags for peers present in group ���
            // — but ONLY for peers that just (re)appeared in the group. Doing it for
            // every peer on every peersList event (which fires constantly on
            // membership/capability churn) wiped the MAX_RETRIES counter before it
            // could climb to 5 and repeatedly un-promoted WS relay, fueling a
            // reconnect/glare storm. Continuously-present peers now keep their retry/
            // relay state; _handlePeerIceFailure's RETRY_WINDOW resets their backoff
            // after a period of stability. A returning peer (network change / sleep-
            // wake) still gets a fresh start because it was absent from _knownPeerIds.
            const currentPeerIds = Object.keys(this.peersList)
            // Fresh: never seen, or back after 5 min away. A peer that dropped
            // and rejoined (its WebSocket reconnected) keeps its backoff: reset,
            // it was retried by every page at every flap (connectBackoff.js)
            for (const peerId of this._connectBackoff.seen(currentPeerIds)) {
                this._iceRetryState.delete(peerId)
                this._wsRelayPeers.delete(peerId)
            }
            this._knownPeerIds = new Set(currentPeerIds)
            // Attempt WebRTC connections to new/returning peers
            this._eventBusConnectToSubscribers()
        })

        this.groupId = groupId

        // Build WebSocket URL from resolved discovery origin. Outside a
        // window there is no page origin to fall back on: the discovery URL
        // must be given (Node, 2026-09-28).
        let base = this.config.discoveryURL
        if (!base || base === '/') {
            if (typeof window === 'undefined') {
                throw new Error('Group: discoveryURL is required outside a window context')
            }
            const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
            base = `${wsProtocol}//${window.location.host}`
        } else {
            base = base.replace(/^http(s?):/, 'ws$1:')
        }
        this.discoveryURL = `${base.replace(/\/+$/, '')}/ws?group=${groupId}`

        console.log('using discoveryURL: ', this.discoveryURL)

        // Register this group with the global registry
        acequia.registerGroup(this)

        this.setupServer()
    }

    startHeartbeat(rate) {
        if (this.heartbeatTimeoutId) clearTimeout(this.heartbeatTimeoutId)
        this._heartbeatBaseRate = rate
        // Back off a little when the page is hidden (saves battery on mobile),
        // but stay well inside the server's 60 s limit (heartbeat.js)
        const effectiveRate = heartbeatDelay(rate, typeof document !== 'undefined' && document.hidden)
        this.heartbeatTimeoutId = setTimeout(() => {
            // Check WS congestion before sending
            if (!this.server || this.server.readyState !== WebSocket.OPEN) {
                console.warn(`[ws] send guard: heartbeat skipped (readyState=${this.server?.readyState ?? 'no socket'})`)
            } else if (this.server.bufferedAmount > 65536 && !this._outboundStreams) {
                // A backlog with no stream of ours behind it: the socket may
                // be stalled, so don't pile on. During our own response stream
                // the backlog is expected (it sits at HIGH_WATER), and skipping
                // starved the relay of heartbeats for the whole transfer —
                // large-uploads project, finding 2, 2026-10-15.
                console.warn(`[ws] bufferedAmount=${this.server.bufferedAmount}, skipping heartbeat (connection may be stalled)`)
            } else {
                this.server.send(
                    JSON.stringify({
                        type: 'heartbeat',
                        deviceInfo: this.getDeviceInfo(),
                    })
                )
            }
            this.startHeartbeat(rate)
        }, effectiveRate)
    }

    setupServer() {
        this.server = new WebSocket(this.discoveryURL)
        // Binary frames from the server (proxiedRequestStream) arrive as
        // ArrayBuffer instead of Blob so the message handler can parse
        // synchronously.
        this.server.binaryType = 'arraybuffer'
        // If WS stays stuck in CONNECTING, force-close so the reconnect
        // backoff loop can retry. Without this, ready hangs forever.
        this._wsConnectTimer = setTimeout(() => {
            if (this.server?.readyState === WebSocket.CONNECTING) {
                console.warn(`[ws] connection timeout after 10s (stuck in CONNECTING), forcing close to trigger reconnect`)
                this.server.close()
            }
        }, 10000)
        this.server.addEventListener('open', (event) => {
            clearTimeout(this._wsConnectTimer)
            if (debug) console.log('Connection from server ', event)
        })

        this.server.addEventListener('close', (event) => {
            clearTimeout(this._wsConnectTimer)
            const code = event.code || 0
            const reason = event.reason || ''
            console.log(`[ws] close code=${code} reason="${reason}" wasClean=${event.wasClean}`)
            this.connected = false
            this.fire('close', event)
            if (this._wsReconnectStableTimer) {
                clearTimeout(this._wsReconnectStableTimer)
                this._wsReconnectStableTimer = null
            }
            if (!this._destroyed) {
                // Exponential backoff with jitter; immediate retry on abnormal close (1006)
                const base = code === 1006 ? 500 : 1000
                const maxDelay = 30000
                const delay = Math.min(maxDelay, base * Math.pow(2, this._wsReconnectAttempt))
                    * (0.5 + Math.random() * 0.5)
                this._wsReconnectAttempt++
                console.log(`[ws] reconnecting in ${Math.round(delay)}ms (attempt ${this._wsReconnectAttempt})`)
                setTimeout(this.setupServer.bind(this), delay)
            }
        })

        this.server.addEventListener('message', async (event) => {
            try {
                this.fire('message', event)
                let data
                if (event.data instanceof ArrayBuffer) {
                    // Binary stream frame (proxiedRequestStream). Returns a
                    // synthesized proxiedRequest once a stream completes;
                    // start/data/abort frames return null (nothing to
                    // dispatch yet).
                    data = this._handleStreamFrame(event.data)
                    if (!data) return
                } else {
                    data = JSON.parse(event.data)
                }

                if (data.type === 'peersList') {
                    if (debug) console.log('peersList: ', data)
                    // Filter out invalid peer entries (null/undefined keys from legacy registrations)
                    const peers = data.data
                    if (peers) {
                        for (const key of Object.keys(peers)) {
                            if (key === 'null' || key === 'undefined') delete peers[key]
                        }
                    }
                    this.peersList = peers
                    this.fire('peersList', peers)
                } else if (data.type === 'messageFromPeer') {
                    let message = data.message
                    let peerId = data.fromPeerId
                    if (!peerId) {
                        if (debug) console.warn('[ws] ignoring messageFromPeer with null fromPeerId')
                        return
                    }
                    if (message.type === 'signal') {
                        this._handlePeerSignal(peerId, message).catch(e => {
                            // An offer while peer creation is paused (the browser's cap) goes unanswered, quietly
                            if (e?.code !== PEER_LIMIT_CODE) console.warn(`[webrtc] signal handling failed for ${peerId.slice(0, 8)}:`, e.message)
                        })
                    } else {
                        // Fire event for custom peer messages (WS relay transport)
                        this.fire('peerMessage', { peerId, message, transport: 'ws' })
                    }
                } else if (data.type === 'proxiedRequest') {
                    await this._dispatchProxiedRequest(data)
                } else if (data.type === 'welcome') {
                    if (debug)
                        console.log(
                            `connected to ${data.group} WebSocketServer`
                        )
                    // Capability negotiation (binary-transfer-performance #3):
                    // only servers that advertise binaryFrames get binary-
                    // framed proxiedResponse bodies; others get base64 JSON.
                    // Set per-welcome so a reconnect to an older server
                    // downgrades correctly.
                    this._serverBinaryFrames = data.binaryFrames === true
                    this._serverStreamFrames = data.streamFrames === true
                    // This socket's challenge: register proves our key with it
                    // (hello.js). A server without one gets no proof.
                    this._hello = data.challenge?.nonce
                        ? { nonce: data.challenge.nonce, domain: data.domain, group: data.group, proof: null, for: null }
                        : null
                    // A reconnect invalidates any half-assembled request
                    // streams from the previous socket.
                    if (this._incomingStreams) {
                        for (const entry of this._incomingStreams.values()) {
                            if (entry.live) entry.controller?.error(new Error('WS reconnected mid stream'))
                        }
                        this._incomingStreams.clear()
                    }
                    // Reset active timestamp so the next visibilitychange
                    // doesn't false-trigger wake recovery — the welcome
                    // handler already does its own resync.
                    this._lastActiveTimestamp = Date.now()
                    if (data.heartbeat > 0) {
                        this.startHeartbeat(data.heartbeat)
                    }
                    this._startZombieCheck()
                    this._startRouteHealthCheck()
                    await this.register()
                    // Notify service worker about group membership
                    this.updateServiceWorkerGroups()

                    // Re-register routes on reconnect — local Router still
                    // holds patterns and handlers, but the server dropped them
                    // when the previous WS connection closed.
                    const localRoutes = this.router.getRouteRegistry()
                    const routeEntries = Object.entries(localRoutes)
                    // Diagnostic: verify route handlers survived WS reconnect
                    const handlersIntact = routeEntries.every(([, entry]) => typeof entry.handler === 'function')
                    console.log(`[route-diag] welcome: ${routeEntries.length} local routes, handlers intact: ${handlersIntact}`, routeEntries.map(([p, e]) => `${p} (handler=${typeof e.handler})`))
                    // A new connection starts the drift confirmation over.
                    // Without this, the candidate set left over from the last
                    // snapshot before the drop would intersect the new
                    // session's empty first snapshot and "confirm" instantly —
                    // reintroducing exactly the false alarm the two-snapshot
                    // rule exists to remove.
                    this._driftCandidates = new Set()
                    this._sentRoutePatterns = new Set()
                    this._registrySynced = false
                    this._preSyncSnapshots = 0
                    if (routeEntries.length > 0) {
                        if (debug) console.log(`re-registering ${routeEntries.length} routes on reconnect`)
                        this._notePatternsSent(routeEntries.map(([pattern]) => pattern))
                        this.server.send(
                            JSON.stringify({
                                type: 'registerRoutes',
                                routes: routeEntries.map(([pattern, entry]) => ({
                                    pattern,
                                    metadata: entry.metadata || {},
                                })),
                                instanceId: this.getDeviceInfo().instanceId,
                            })
                        )
                    }

                    // Join server-side capability lists for leader election
                    const caps = this.config.capabilities || []
                    for (const cap of caps) {
                        this.server.send(JSON.stringify({
                            type: 'capabilityJoin',
                            capability: cap,
                            instanceId: this.getDeviceInfo().instanceId,
                        }))
                    }

                    // Prune peers whose data channels died during the WS outage
                    for (const [peerId, peer] of Object.entries(this._peers)) {
                        if (peer._destroyed || peer._channel?.readyState !== 'open') {
                            console.warn(`[webrtc] pruning dead peer ${peerId.slice(0, 8)} on reconnect (channel=${peer._channel?.readyState ?? 'none'})`)
                            delete this._peers[peerId]
                            if (!peer._destroyed) peer.destroy()
                        }
                    }

                    this.connected = true
                    // Reset WS reconnect backoff after connection is stable for 10s
                    if (this._wsReconnectStableTimer) clearTimeout(this._wsReconnectStableTimer)
                    this._wsReconnectStableTimer = setTimeout(() => {
                        if (this.connected) this._wsReconnectAttempt = 0
                    }, 10000)
                    this.fire('welcome', event)
                } else if (data.type === 'capabilityList') {
                    this._handleCapabilityList(data)
                    this.fire('capabilityList', data)
                } else if (data.type === 'capabilityPing') {
                    // Respond with liveness + WebRTC health
                    const peerIds = Object.keys(this._peers)
                    const openChannels = peerIds.filter(id =>
                        this._peers[id]?._channel?.readyState === 'open'
                    ).length
                    if (debug) console.log(`[state] ping received for ${data.capability}, ponging (${openChannels}/${peerIds.length} peers)`)
                    if (this.server?.readyState !== WebSocket.OPEN) {
                        console.warn(`[ws] send guard: capabilityPong dropped (readyState=${this.server?.readyState ?? 'no socket'})`)
                        return
                    }
                    this.server.send(JSON.stringify({
                        type: 'capabilityPong',
                        capability: data.capability,
                        instanceId: acequia.getInstanceId(),
                        health: {
                            connectedPeers: openChannels,
                            totalPeers: peerIds.length,
                            stateVersion: this._isStateLeader ? this._stateVersion : undefined,
                        },
                    }))
                } else if (data.type === 'routeRegistry') {
                    acequia.postToSW({
                        type: 'ROUTE_REGISTRY',
                        data,
                    })
                    // Drift detection: check if our local routes are present in the server's registry
                    const serverRoutes = data.data || {}
                    const localRoutes = this.router.getRouteRegistry()
                    // Compare against the routes we have SENT, not everything
                    // registered locally. An app registers its routes one at a
                    // time, each send triggering its own registry publish, so a
                    // route the app has created locally but not yet announced is
                    // legitimately absent from the server — and treating that as
                    // drift produced the descending `8/8, 7/8, 6/8 …` storm that
                    // looks exactly like a peer losing its registration.
                    const localPatterns = Object.keys(localRoutes).filter(p =>
                        (this._sentRoutePatterns || new Set()).has(p)
                    )
                    if (localPatterns.length > 0) {
                        const absent = localPatterns.filter(p => !serverRoutes[p])
                        // Drift means "it was there and went away", which
                        // requires having SEEN it there. Until every announced
                        // route has appeared in one snapshot, the registry is
                        // merely catching up: the sends all leave at once but
                        // the server publishes after each registration, so the
                        // snapshots legitimately arrive partial — 1 route, then
                        // 2, then 3. Reporting that produced the descending
                        // `8/8, 7/8, 6/8 …` storm that reads like a peer losing
                        // its registration, and cost a 2026-09-19 session.
                        //
                        // A registration that genuinely never lands must still
                        // be recovered, so the grace is bounded: after
                        // PRE_SYNC_GRACE snapshots still short, treat it as
                        // drift and re-register.
                        // Scaled to what was announced: a peer that sends N
                        // routes at once sees N partial snapshots before the
                        // registry is whole (BrowserDAV sends eight; the fixed
                        // 3 fired on every start, 2026-09-28).
                        const PRE_SYNC_GRACE = Math.max(3, localPatterns.length + 1)
                        if (!this._registrySynced) {
                            if (absent.length === 0) {
                                this._registrySynced = true
                                this._preSyncSnapshots = 0
                            } else {
                                this._preSyncSnapshots = (this._preSyncSnapshots || 0) + 1
                            }
                            if (!this._registrySynced && this._preSyncSnapshots <= PRE_SYNC_GRACE) {
                                this._driftCandidates = new Set(absent)
                                return
                            }
                        }
                        // Drift must be CONFIRMED by two consecutive snapshots.
                        // The server publishes the registry on connect — before
                        // the welcome handler has re-registered anything — so a
                        // single empty snapshot is the normal shape of every
                        // connect, not drift. Acting on it re-registered all
                        // routes a second time on every connect (v1–v8 then
                        // v9–v16 in the server log), and the warning it printed
                        // sent a 2026-09-19 debugging session after a lost
                        // socket that was never lost: the tab was merely
                        // backgrounded, and Chrome's timer throttling had
                        // stretched the reconnect from 3s to 36s.
                        //
                        // A real loss persists across snapshots, so confirming
                        // costs one broadcast of latency and nothing else.
                        const prior = this._driftCandidates || new Set()
                        const missing = absent.filter(p => prior.has(p))
                        this._driftCandidates = new Set(absent)
                        if (missing.length > 0) {
                            console.warn(`[route-drift] ${missing.length}/${localPatterns.length} local routes missing from server registry (confirmed over two snapshots):`, missing)
                            this.fire('routeDrift', { missing, total: localPatterns.length })
                            // Re-register missing routes
                            if (this.server?.readyState === WebSocket.OPEN) {
                                const instanceId = this.getDeviceInfo().instanceId
                                this.server.send(JSON.stringify({
                                    type: 'registerRoutes',
                                    routes: missing.map(pattern => ({
                                        pattern,
                                        metadata: localRoutes[pattern].metadata || {},
                                    })),
                                    instanceId,
                                }))
                                console.warn(`[route-drift] re-registered ${missing.length} missing routes`)
                            }
                        }
                    }
                } else {
                    if (debug) console.log('unknown message from server ', data)
                }
            } catch (e) {
                this.fire('error', event, e)
            }
        })
    }

    setDisplayName(displayName) {
        this.displayName = displayName
    }

    getDeviceInfo() {
        return {
            deviceId: acequia.getDeviceId(),
            userId: acequia.getUserId(),
            instanceId: acequia.getInstanceId(),
            handle: acequia.getHandle?.(),
            deviceName: acequia.getDeviceName?.(),
            instanceName: acequia.getInstanceName?.(),
            displayName: this.displayName,
            group: this.groupId,
            capabilities: this.config.capabilities,
            // Transport capability: this peer understands peerRequestStream /
            // peerResponseStream messages over the WebRTC data channel
            // (browserdav-transport-robustness). Propagates via peersList so
            // senders know before streaming.
            streamMessages: true,
            ...this._extraDeviceInfo,
        }
    }

    updateDeviceInfo(info) {
        this._extraDeviceInfo = { ...this._extraDeviceInfo, ...info }
        this.register()
    }

    async register() {
        // A fresh identity is minted asynchronously (acequia.js ensureGUIDs).
        // A group constructed before it lands gets its welcome first, and
        // registering then sends a deviceInfo with no instanceId/userId: the
        // relay files the client under nothing and its peersList never
        // arrives. A first-run Node process (a one-shot copier) hit this every
        // time, 2026-10-15 (large-uploads project, finding c).
        // acequiaReady() always settles, even on a failed boot.
        if (!acequia.getInstanceId()) await acequia.acequiaReady()
        const deviceInfo = this.getDeviceInfo()
        const proof = await this._helloProof(deviceInfo)
        if (this.server && this.server.readyState === WebSocket.OPEN) {
            this.server.send(
                // streamFrames: this peer can parse binary
                // proxiedRequestStream frames (start/data/end/abort) — the
                // relay only streams request bodies to peers advertising it.
                JSON.stringify({ type: 'register', deviceInfo, streamFrames: true, ...(proof ? { proof } : {}) })
            )
        }
    }

    /**
     * The proof of this peer's key for this socket (hello.js), signed once
     * per socket and identity: registering again with the same ids sends
     * the same proof, which the server does not check twice. Never fails a
     * registration — a peer that cannot sign registers unproven, as before.
     */
    async _helloProof(deviceInfo) {
        const hello = this._hello
        if (!hello?.nonce) return null
        const ids = `${deviceInfo.instanceId}|${deviceInfo.deviceId}|${deviceInfo.userId}`
        if (hello.proof && hello.for === ids) return hello.proof
        try {
            const proof = await signHello({ keys: acequia.getUserKeys?.(), nonce: hello.nonce, domain: hello.domain, group: hello.group, deviceInfo })
            if (this._hello === hello) Object.assign(hello, { proof, for: ids })
            return proof
        } catch (err) {
            if (!this._helloWarned) console.warn('[group] could not sign the hello; registering unproven:', err?.message || err)
            this._helloWarned = true
            return null
        }
    }

    /**
     * Remember that a pattern has been announced to the server, so drift
     * detection can tell "the server lost my route" from "I have not told the
     * server about it yet". Only sends on an OPEN socket count; a deferred
     * registration is re-announced by the welcome handler on reconnect, which
     * resets this set to exactly what it re-sends.
     */
    _notePatternsSent(patterns) {
        if (!this._sentRoutePatterns) this._sentRoutePatterns = new Set()
        let added = false
        for (const p of patterns) {
            if (!this._sentRoutePatterns.has(p)) added = true
            this._sentRoutePatterns.add(p)
        }
        // Newly announced routes have not been seen in a snapshot yet, so the
        // registry is once again catching up.
        if (added) {
            this._registrySynced = false
            this._preSyncSnapshots = 0
        }
    }

    registerRoute(pattern, handler, metadata = {}) {
        // Register locally first so routes survive even if WS send fails
        this.router.registerRoute(pattern, handler, metadata)
        if (this.server?.readyState === WebSocket.OPEN) {
            this.server.send(
                JSON.stringify({
                    type: 'registerRoute',
                    pattern,
                    metadata,
                    instanceId: this.getDeviceInfo().instanceId,
                })
            )
            this._notePatternsSent([pattern])
        } else {
            console.warn(`[ws] registerRoute deferred (readyState=${this.server?.readyState ?? 'no socket'}, pattern=${pattern}) — will re-register on reconnect`)
        }
    }

    registerRoutes(routes) {
        const instanceId = this.getDeviceInfo().instanceId
        // Register locally first so routes survive even if WS send fails
        for (const r of routes) {
            this.router.registerRoute(r.pattern, r.handler, r.metadata || {})
        }
        if (this.server?.readyState === WebSocket.OPEN) {
            this.server.send(
                JSON.stringify({
                    type: 'registerRoutes',
                    routes: routes.map(r => ({ pattern: r.pattern, metadata: r.metadata || {} })),
                    instanceId,
                })
            )
            this._notePatternsSent(routes.map(r => r.pattern))
        } else {
            console.warn(`[ws] registerRoutes deferred (readyState=${this.server?.readyState ?? 'no socket'}, ${routes.length} routes) — will re-register on reconnect`)
        }
    }

    unregisterRoute(pattern) {
        if (this.server) {
            this.server.send(
                JSON.stringify({
                    type: 'unregisterRoute',
                    pattern,
                    instanceId: this.getDeviceInfo().instanceId,
                })
            )
            // Deliberately gone: stop expecting it, or drift reports it forever.
            this._sentRoutePatterns?.delete(pattern)
            this._driftCandidates?.delete(pattern)
        }
        this.router.unregisterRoute(pattern)
    }

    unregisterRoutes(patterns) {
        const instanceId = this.getDeviceInfo().instanceId
        if (this.server) {
            this.server.send(
                JSON.stringify({
                    type: 'unregisterRoutes',
                    patterns,
                    instanceId,
                })
            )
        }
        for (const pattern of patterns) {
            this.router.unregisterRoute(pattern)
            // Deliberately gone: stop expecting it, or drift reports it forever.
            this._sentRoutePatterns?.delete(pattern)
            this._driftCandidates?.delete(pattern)
        }
    }

    /**
     * Handle a proxiedRequest: match a route, normalize the body, run the
     * handler, and send the response back over the relay. Streamed requests
     * for routes registered with `metadata.streaming` carry `bodyStream` (a
     * ReadableStream) instead of a buffered body. Streamed *responses*
     * (handler returns a ReadableStream body, or a Blob past the threshold)
     * go out as proxiedResponseStream frames when the server supports them.
     */
    async _dispatchProxiedRequest(data) {
        try {
            let match = this.router.matchRoute(data.path)
            if (debug) console.log('got match: ', match)
            let body = null
            let rawBody = null
            if (!data.bodyStream) {
                let rawInput = data.body
                if (typeof Blob !== 'undefined' && rawInput instanceof Blob) {
                    rawInput = await rawInput.arrayBuffer()
                }
                ;({ body, rawBody } = normalizeBody(rawInput, data.headers?.['content-type']))
            }
            const result = await match.handler({
                ...data,
                body,
                rawBody,
                pathParams: match.pathParams,
                remainingPath: match.remainingPath,
            })
            if (this._shouldStreamResponse(result?.body)) {
                await this._sendResponseStream(data.requestId, result)
                return
            }
            // A ReadableStream body with no stream-capable server must be
            // buffered before the legacy single-frame path can carry it.
            if (typeof ReadableStream !== 'undefined' && result?.body instanceof ReadableStream) {
                result.body = await new Response(result.body).blob()
            }
            // keepBinary: when the server advertised binaryFrames support,
            // binary bodies stay raw and ship as a binary WS frame
            // (sendProxiedResponse) instead of base64.
            const responseData = await normalizeResponse(result, {
                keepBinary: this._serverBinaryFrames === true,
            })
            this.sendProxiedResponse({
                requestId: data.requestId,
                data: responseData,
            })
        } catch (e) {
            // Projection only — data is the raw proxied request
            // (may carry auth headers / body).
            console.warn('got proxiedRequest error', e?.message || e, {
                requestId: data?.requestId,
                method: data?.method,
                path: data?.path,
            })

            this.sendProxiedResponse({
                requestId: data.requestId,
                data: createErrorResponse(e),
            })
        }
    }

    /**
     * Blobs past this size stream as frames instead of one giant WS frame.
     * The default of the per-group `transport.streamThreshold`; kept as a
     * static for callers that predate transport config.
     */
    static STREAM_RESPONSE_THRESHOLD = DEFAULT_TRANSPORT.streamThreshold

    _shouldStreamResponse(body) {
        if (!this._serverStreamFrames) return false
        if (typeof ReadableStream !== 'undefined' && body instanceof ReadableStream) return true
        return (
            typeof Blob !== 'undefined' &&
            body instanceof Blob &&
            body.size > (this.transport?.streamThreshold ?? Group.STREAM_RESPONSE_THRESHOLD)
        )
    }

    /** Encode one stream frame: [4-byte BE uint32 metaLen][JSON meta][chunk]. */
    _encodeStreamFrame(meta, chunkParts = null) {
        const metaBytes = new TextEncoder().encode(JSON.stringify(meta))
        const header = new ArrayBuffer(4)
        new DataView(header).setUint32(0, metaBytes.byteLength)
        return chunkParts
            ? new Blob([header, metaBytes, ...chunkParts])
            : new Blob([header, metaBytes])
    }

    /**
     * Send a handler response whose body is a ReadableStream or large Blob
     * as proxiedResponseStream frames (start carries status/headers, data
     * frames carry body chunks, end closes). Peak memory is one chunk on
     * both sides; the server pipes frames straight to the HTTP response.
     * Backpressure: pauses while the WS send buffer is above high water.
     */
    async _sendResponseStream(requestId, response) {
        const streamId = requestId
        const type = 'proxiedResponseStream'
        const CHUNK = 512 * 1024
        const HIGH_WATER = 4 * 1024 * 1024
        const send = async (meta, chunkParts = null) => {
            while (this.server.bufferedAmount > HIGH_WATER) {
                await new Promise((r) => setTimeout(r, 50))
            }
            if (this.server.readyState !== WebSocket.OPEN) {
                throw new Error('WS closed mid response stream')
            }
            this.server.send(this._encodeStreamFrame(meta, chunkParts))
        }

        let seq = 0
        const body = response.body
        // bodyFlush: 'eager' — a line-oriented report (a pull's performance
        // markers) goes out as it is produced, one frame per chunk, instead
        // of waiting for CHUNK bytes to accumulate.
        const eager = response.bodyFlush === 'eager'
        const meta = { ...response, body: undefined, bodyEncoding: 'stream' }
        await send({ type, kind: 'start', streamId, seq, response: { requestId, data: meta } })

        const reader = (body instanceof Blob ? body.stream() : body).getReader()
        let total = 0
        let pending = []
        let pendingBytes = 0
        const flush = async () => {
            if (!pendingBytes) return
            total += pendingBytes
            seq++
            await send({ type, kind: 'data', streamId, seq }, pending)
            pending = []
            pendingBytes = 0
        }
        // While this runs the send buffer sits near HIGH_WATER by design; the
        // heartbeat reads that as our own backlog, not a stall (startHeartbeat).
        this._outboundStreams = (this._outboundStreams || 0) + 1
        try {
            for (;;) {
                const { done, value } = await reader.read()
                if (done) break
                pending.push(value)
                pendingBytes += value.byteLength
                if (pendingBytes >= CHUNK || eager) await flush()
            }
            await flush()
            seq++
            await send({ type, kind: 'end', streamId, seq, totalBytes: total })
        } catch (e) {
            console.warn(`[stream] response stream ${streamId} failed:`, e?.message || e)
            try {
                await send({ type, kind: 'abort', streamId, seq: seq + 1, error: String(e?.message || e) })
            } catch {
                // socket already dead
            }
            reader.cancel?.().catch?.(() => {})
        } finally {
            this._outboundStreams--
        }
    }

    /**
     * Handle one binary stream frame from the server
     * ([4-byte BE uint32 metaLen][JSON meta][chunk bytes], see
     * localDiscovery/src/streamFrames.mjs). Frames sharing a streamId are
     * assembled: 'start' carries the request metadata, 'data' frames carry
     * body chunks, 'end' completes the stream.
     *
     * Routes registered with `metadata.streaming` are dispatched at 'start'
     * with a live ReadableStream body (`bodyStream`) fed by subsequent
     * frames. All other routes buffer: the synthesized proxiedRequest (body
     * as Blob — chunks are not copied until read) is returned on 'end', or
     * null while the stream is still in flight.
     */
    /** Bytes a live request-body stream may hold before the server is told to pause. */
    static STREAM_BODY_HIGH_WATER = 8 * 1024 * 1024

    /** Tell the relay to stop or go on reading a streamed request body (streamFlow). */
    _sendStreamFlow(streamId, kind) {
        if (!this.server || this.server.readyState !== WebSocket.OPEN) return
        this.server.send(JSON.stringify({ type: 'streamFlow', streamId, kind }))
    }

    _handleStreamFrame(buf) {
        if (buf.byteLength < 4) throw new Error('stream frame too short')
        const metaLen = new DataView(buf).getUint32(0)
        if (4 + metaLen > buf.byteLength) throw new Error('stream frame meta length out of bounds')
        const meta = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 4, metaLen)))
        const chunk = buf.slice(4 + metaLen)

        if (!this._incomingStreams) this._incomingStreams = new Map()
        const streams = this._incomingStreams

        if (meta.kind === 'start') {
            // Streaming routes get the request now, with a live body stream.
            let streamingMatch = null
            try {
                const match = this.router.matchRoute(meta.path)
                if (match?.metadata?.streaming) streamingMatch = match
            } catch {
                // No route — buffered path will produce the 404 on 'end'
            }
            if (streamingMatch) {
                const entry = { meta, seq: meta.seq, live: true, paused: false }
                // Backpressure (2026-10-19): the stream has a byte-counted
                // high-water mark. When the handler (a PUT writing to disk)
                // falls behind the frames, the queue fills, desiredSize drops
                // to zero and a `streamFlow: pause` goes to the server, which
                // stops reading the HTTP body; when the handler drains the
                // queue, pull() sends `resume`. Peak memory in the tab is the
                // high-water mark instead of the whole file.
                const sid = meta.streamId
                const group = this
                entry.bodyStream = new ReadableStream(
                    {
                        start(controller) {
                            entry.controller = controller
                        },
                        pull() {
                            if (entry.paused) {
                                entry.paused = false
                                group._sendStreamFlow(sid, 'resume')
                            }
                        },
                    },
                    new ByteLengthQueuingStrategy({ highWaterMark: Group.STREAM_BODY_HIGH_WATER }),
                )
                streams.set(meta.streamId, entry)
                const { kind, seq, streamId, ...request } = meta
                request.type = 'proxiedRequest'
                request.body = null
                request.bodyStream = entry.bodyStream
                // Handler runs while frames keep arriving — do not await.
                this._dispatchProxiedRequest(request)
                return null
            }
            streams.set(meta.streamId, { meta, chunks: [], seq: meta.seq })
            return null
        }
        const stream = streams.get(meta.streamId)
        if (!stream) {
            console.warn(`[stream] frame for unknown stream ${meta.streamId} (kind=${meta.kind}) — dropped`)
            return null
        }
        if (meta.seq !== stream.seq + 1) {
            // WS is ordered — a gap means the stream is corrupt. Fail the
            // request rather than assemble a bad body.
            console.warn(`[stream] seq gap on ${meta.streamId} (got ${meta.seq}, expected ${stream.seq + 1}) — aborting stream`)
            streams.delete(meta.streamId)
            if (stream.live) {
                stream.controller?.error(new Error('stream sequence error'))
            } else {
                this.sendProxiedResponse({
                    requestId: stream.meta.requestId,
                    data: { status: 400, body: 'stream sequence error', headers: { 'content-type': 'text/plain' } },
                })
            }
            return null
        }
        stream.seq = meta.seq

        if (meta.kind === 'data') {
            if (stream.live) {
                stream.controller?.enqueue(new Uint8Array(chunk))
                const room = stream.controller?.desiredSize
                if (!stream.paused && typeof room === 'number' && room <= 0) {
                    stream.paused = true
                    this._sendStreamFlow(meta.streamId, 'pause')
                }
            } else {
                stream.chunks.push(chunk)
            }
            return null
        }
        if (meta.kind === 'abort') {
            console.warn(`[stream] ${meta.streamId} aborted by server: ${meta.error || 'unknown'}`)
            streams.delete(meta.streamId)
            if (stream.live) stream.controller?.error(new Error(meta.error || 'aborted by server'))
            return null
        }
        if (meta.kind === 'end') {
            streams.delete(meta.streamId)
            if (stream.live) {
                stream.controller?.close()
                return null
            }
            const { kind, seq, streamId, ...request } = stream.meta
            request.type = 'proxiedRequest'
            request.body = new Blob(stream.chunks)
            return request
        }
        console.warn(`[stream] unknown frame kind ${meta.kind} on ${meta.streamId} — dropped`)
        return null
    }

    async sendProxiedResponse(response) {
        if (!this.server || this.server.readyState !== WebSocket.OPEN) {
            console.warn(`[ws] send guard: proxiedResponse dropped (readyState=${this.server?.readyState ?? 'no socket'})`)
            return
        }
        try {
            const body = response?.data?.body
            const isBinaryBody =
                (typeof Blob !== 'undefined' && body instanceof Blob) ||
                body instanceof ArrayBuffer ||
                ArrayBuffer.isView(body)
            if (isBinaryBody) {
                if (this._serverBinaryFrames) {
                    // Binary WS frame (binary-transfer-performance #3):
                    // [4-byte BE uint32 meta length][JSON meta][raw body].
                    // Body rides raw — 1× instead of base64's 1.33×.
                    const meta = {
                        type: 'proxiedResponse',
                        response: {
                            ...response,
                            data: { ...response.data, body: undefined, bodyEncoding: 'binary-frame' },
                        },
                    }
                    const metaBytes = new TextEncoder().encode(JSON.stringify(meta))
                    const header = new ArrayBuffer(4)
                    new DataView(header).setUint32(0, metaBytes.byteLength)
                    const bodyPart = body instanceof Blob ? body : new Blob([body])
                    this.server.send(new Blob([header, metaBytes, bodyPart]))
                    return
                }
                // Server didn't advertise binaryFrames — fall back to base64.
                response = { ...response, data: await normalizeResponse(response.data) }
            }
            const responseStr = JSON.stringify({
                type: 'proxiedResponse',
                response,
            })
            if (debug) console.log('responseStr: ', responseStr)
            this.server.send(responseStr)
        } catch (e) {
            // Fire-and-forget caller — never let a framing error escape as an
            // unhandled rejection.
            console.warn('[ws] proxiedResponse send failed:', e?.message || e)
        }
    }

    serverSendToPeer(peerId, message) {
        if (!this.server || this.server.readyState !== WebSocket.OPEN) {
            console.warn(`[ws] send guard: sendToPeer dropped (readyState=${this.server?.readyState ?? 'no socket'}, peer=${peerId?.slice(0, 8)})`)
            return
        }
        if (debug) console.log('serverSendToPeer: ', message)
        this.server.send(
            JSON.stringify({
                type: 'sendToPeer',
                peerId,
                message,
            })
        )
    }

    /**
     * Send a message to a peer using the best available transport.
     * Transport cascade: SW postMessage > WebRTC > WS relay.
     * Returns the transport used: 'sw' | 'webrtc' | 'ws'
     */
    async sendToPeer(peerId, message) {
        if (!peerId) return null
        const peerInfo = this.peersList?.[peerId]

        // 1. Same device — SW delivers via client.postMessage (acknowledged)
        if (peerInfo?.deviceId === acequia.getDeviceId()) {
            const delivered = await this._sendViaSW(peerId, message)
            if (delivered) return 'sw'
            // SW couldn't deliver (target not in instanceMap) — fall through
        }

        // 2. WebRTC — open data channel
        const peer = this._peers[peerId]
        if (peer && !peer._destroyed && peer._channel?.readyState === 'open') {
            peer.sendBig({ type: 'group:peerMessage', message })
            return 'webrtc'
        }

        // 3. WS relay — send now, start connecting for next time
        this.serverSendToPeer(peerId, message)
        this._ensureConnecting(peerId)
        return 'ws'
    }

    /**
     * Transport-agnostic ping. Sends via sendToPeer, awaits pong via peerMessage.
     * Returns { rtt, pingTransport, pongTransport, inboundTransport, status? }
     * where pingTransport is the outbound leg, pongTransport is the return leg,
     * and inboundTransport is what the responder saw for the incoming ping.
     */
    async pingPeer(peerId, options = {}) {
        const pingId = Math.random().toString(36).slice(2)
        const sent = Date.now()
        const pingTransport = await this.sendToPeer(peerId, {
            type: 'group:ping',
            pingId,
            timestamp: sent,
            requestStatus: !!options.requestStatus,
        })

        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.off('peerMessage', handler)
                reject(new Error('Ping timeout'))
            }, options.timeout || 5000)

            const handler = ({ message, transport }) => {
                if (message?.type === 'group:pong' && message.pingId === pingId) {
                    clearTimeout(timeout)
                    this.off('peerMessage', handler)
                    resolve({
                        rtt: Date.now() - sent,
                        pingTransport,
                        pongTransport: transport,
                        inboundTransport: message.inboundTransport,
                        status: message.status || null,
                    })
                }
            }
            this.on('peerMessage', handler)
        })
    }

    /**
     * Send a peer message via service worker client.postMessage().
     * Returns true if the SW is active and the message was posted, false otherwise.
     * Fire-and-forget — delivery not guaranteed (stale client handle possible).
     */
    async _sendViaSW(peerId, message) {
        if (!navigator?.serviceWorker?.controller) {
            console.log(`[sw-delivery] no SW controller`)
            return false
        }
        const requestId = Math.random().toString(36).slice(2, 10)
        acequia.postToSW({
            type: 'group:peerMessage',
            requestId,
            targetInstanceId: peerId,
            groupId: this.groupId,
            fromInstanceId: acequia.getInstanceId(),
            message,
        })
        return acequia.awaitSWDelivery(requestId)
    }

    /**
     * Opportunistically start a WebRTC connection to a peer if not already
     * connected or connecting. Used by sendToPeer when falling back to WS relay
     * so that subsequent messages can use WebRTC.
     */
    _ensureConnecting(peerId) {
        if (!peerId) return
        // A passive peer answers offers and never makes one: a serving
        // process (a registered BrowserDAV in Node, 2026-09-28) has nothing
        // to say first, and two sides offering at once thrashed the glare
        // handling every time a browser connected to it.
        if (this.config.passivePeer) return
        if (this._peers[peerId]) return
        if (this._connectingPeers.has(peerId)) return
        // Every relayed message used to start another attempt
        if (this._connectBackoff.blocked(peerId) || peerLimitActive()) return
        this._connectingPeers.add(peerId)
        this.connectToPeer(peerId)
            .catch((err) => { if (err?.code !== PEER_LIMIT_CODE) this._connectBackoff.fail(peerId) }) // WS relay continues to work
            .finally(() => this._connectingPeers.delete(peerId))
    }

    /**
     * Update service worker with current group memberships
     */
    updateServiceWorkerGroups() {
        const instanceId = acequia.getInstanceId()
        const groups = Array.from(acequia.activeGroups.keys())

        acequia.postToSW({
            type: 'UPDATE_GROUPS',
            instanceId,
            groups,
        })
    }

    /**
     * Get peer info by instanceId from the peersList
     * @param {string} instanceId
     * @returns {object|null}
     */
    getPeerInfoByInstanceId(instanceId) {
        return this.peersList[instanceId] || null
    }

    /**
     * Get existing peer connection by instanceId
     * @param {string} instanceId
     * @returns {object|null}
     */
    getPeerByInstanceId(instanceId) {
        const peer = this._peers[instanceId]
        if (peer && !peer._destroyed && peer._channel?.readyState === 'open') return peer
        return null
    }

    // ------------------------------------------------------------------
    // Per-group peer signal handling (moved from webrtc.js peerSignalCB)
    // ------------------------------------------------------------------

    /**
     * Handle an incoming WebRTC signal for a peer in this group.
     * Creates responder peers when needed, handles glare resolution.
     */
    async _handlePeerSignal(peerId, signal) {
        let peer = this._peers[peerId]
        const shortId = peerId.slice(0, 8)
        const sigType = signal.data?.type || (signal.data?.candidate ? 'candidate' : 'unknown')
        if (debug && (sigType === 'offer' || sigType === 'answer')) {
            console.log(`[webrtc] ${shortId} received signal: ${sigType} (peer ${peer ? 'exists' : 'new'})`)
        }

        if (!peer) {
            // Only an offer creates a responder. A trickled candidate can
            // overtake its own offer here: every signal for an unknown peer
            // awaits the ICE-server fetch below, and on a fresh process (no
            // cached ICE list) they resume in no fixed order. A candidate
            // that won used to create the responder and feed itself in first;
            // the offer then found a young responder and was dropped as a
            // retry ("ignoring offer"), so no answer ever went out — a Node
            // BrowserDAV share answered some first connections and not
            // others, 2026-10-15 (large-uploads project, finding b). Hold
            // early signals and replay them once the offer has made the peer.
            if (sigType !== 'offer') {
                this._holdEarlySignal(peerId, signal)
                return
            }
            // Normal responder path — create peer as answerer
            const iceServers = await getIceServers()
            // Re-check after async gap — another signal handler or connectToPeer
            // may have already created a peer for this peerId
            peer = this._peers[peerId]
            if (peer) {
                console.log(`[webrtc] ${shortId} peer created during ICE fetch, deferring to glare-aware handler`)
            } else {
                peer = createPeer({
                    peerId,
                    group: this,
                    initiator: false,
                    trickle: true,
                    iceServers,
                    transport: this.transport,
                })
                this._peers[peerId] = peer
                this._wirePeerListeners(peer, peerId)
                this._answerOffer(peer, signal)
                this._replayEarlySignals(peerId, peer)
                return
            }
        }

        const isOffer = signal.data?.type === 'offer'

        // If peer is already connected with an open data channel, handle
        // renegotiation signals (addTrack triggers new offer/answer cycle).
        if (peer.connected || peer._channel?.readyState === 'open') {
            const isAnswer = signal.data?.type === 'answer'
            if (isAnswer && peer._pc?.signalingState === 'stable') {
                if (debug) console.log(`[webrtc] ignoring stale answer for stable peer ${shortId}`)
                return
            }
            if (peer.destroyed || peer.destroying) return
            peer.signal(signal.data)
            return
        }

        // Glare detection: we already have an initiator peer but received an offer
        if (isOffer && peer.initiator) {
            const myId = acequia.getInstanceId()
            const polite = myId > peerId // higher instanceId yields
            if (polite) {
                if (debug) console.log(`[webrtc] glare with ${shortId}, yielding (polite)`)
                const iceServers = await getIceServers()
                // Destroy old initiator, become responder
                const existing = this._peers[peerId]
                if (existing) {
                    delete this._peers[peerId]
                    existing.destroy()
                }
                peer = createPeer({
                    peerId,
                    group: this,
                    initiator: false,
                    trickle: true,
                    iceServers,
                    transport: this.transport,
                })
                this._peers[peerId] = peer
                this._wirePeerListeners(peer, peerId)
                this._answerOffer(peer, signal)
                this._replayEarlySignals(peerId, peer)
            } else {
                // Impolite: ignore their offer, our offer wins.
                const age = Date.now() - (peer._createdAt || 0)
                if (!peer.connected && age > 15000) {
                    if (debug) console.log(`[webrtc] impolite peer ${shortId} stale (${age}ms), recreating initiator`)
                    const iceServers = await getIceServers()
                    const existing = this._peers[peerId]
                    if (existing) {
                        delete this._peers[peerId]
                        existing.destroy()
                    }
                    peer = createPeer({
                        peerId,
                        group: this,
                        initiator: true,
                        trickle: true,
                        iceServers,
                        transport: this.transport,
                    })
                    this._peers[peerId] = peer
                    this._wirePeerListeners(peer, peerId)
                } else {
                    if (debug) console.log(`[webrtc] glare with ${shortId}, ignoring (impolite)`)
                }
            }
            return
        }

        // If we're a responder and receive a new offer, the remote side
        // may be retrying. Only replace if current peer had time to negotiate
        // — unless the offer comes from a new connection (another SDP
        // session): the remote reloaded, or dropped the connection this
        // responder answers. That responder can never connect, and ignoring
        // the new offer left a reloaded page unconnected for up to 30 s
        // (CameraMap reloaded mid-negotiation with a camera tab, 2026-10-01).
        if (isOffer && !peer.initiator) {
            const age = Date.now() - (peer._createdAt || 0)
            const session = sdpSessionId(signal.data?.sdp)
            const newConnection = !!(session && peer._remoteSession && session !== peer._remoteSession)
            if (age < 30000 && !newConnection) {
                if (debug) console.log(`[webrtc] responder ${shortId} ignoring offer (peer age ${age}ms)`)
                return
            }
            if (debug) console.log(`[webrtc] responder ${shortId} received new offer ${newConnection ? 'from a new connection' : `after ${age}ms`}, recreating`)
            const iceServers = await getIceServers()
            const existing = this._peers[peerId]
            if (existing) {
                delete this._peers[peerId]
                existing.destroy()
            }
            peer = createPeer({
                peerId,
                group: this,
                initiator: false,
                trickle: true,
                iceServers,
                transport: this.transport,
            })
            this._peers[peerId] = peer
            this._wirePeerListeners(peer, peerId)
            this._answerOffer(peer, signal)
            this._replayEarlySignals(peerId, peer)
            return
        }

        // Block stale answers when RTCPeerConnection is already stable
        if (signal.data?.type === 'answer' && peer._pc?.signalingState === 'stable') {
            if (debug) console.log(`[webrtc] ignoring stale answer for stable (not-yet-connected) peer ${shortId}`)
            return
        }

        // Normal signal forwarding (answers, ICE candidates)
        if (peer.destroyed || peer.destroying) return
        peer.signal(signal.data)
    }

    /**
     * Keep a signal that arrived for a peer no offer has created yet. Held
     * per peer for 30 s (the connect timeout) and then dropped — an offer
     * that never comes leaves nothing to apply them to.
     */
    _holdEarlySignal(peerId, signal) {
        if (!this._earlySignals) this._earlySignals = new Map()
        let held = this._earlySignals.get(peerId)
        if (!held) {
            held = { signals: [], timer: setTimeout(() => this._earlySignals?.delete(peerId), 30000) }
            held.timer.unref?.() // never the reason a Node process stays up
            this._earlySignals.set(peerId, held)
        }
        held.signals.push(signal)
    }

    /** Apply the signals held for a peer, in arrival order, after its offer. */
    _replayEarlySignals(peerId, peer) {
        const held = this._earlySignals?.get(peerId)
        if (!held) return
        this._earlySignals.delete(peerId)
        clearTimeout(held.timer)
        for (const signal of held.signals) {
            if (peer.destroyed || peer.destroying) return
            peer.signal(signal.data)
        }
    }

    /** A new responder answers an offer: remember which connection it answers (sdpSessionId). */
    _answerOffer(peer, signal) {
        if (signal.data?.type === 'offer') peer._remoteSession = sdpSessionId(signal.data.sdp)
        peer.signal(signal.data)
    }

    /**
     * A peer's ready promise: resolved at once when its channel is open, else
     * a wait for it. A wait that failed isn't handed out again: it used to stay
     * on the peer, so every later connectToPeer failed at once with the old
     * "peer never became ready".
     */
    _readyFor(peer, instanceId, timeout) {
        if (peer.connected || peer._channel?.readyState === 'open') return Promise.resolve(peer)
        if (!peer.ready || peer._readyFailed) {
            peer._readyFailed = false
            const wait = this._waitForPeer(instanceId, timeout)
            wait.catch(() => { peer._readyFailed = true })
            peer.ready = wait
        }
        return peer.ready
    }

    /**
     * Wait for a peer in this group to be ready (data channel open).
     * @param {string} peerId
     * @param {number} timeout - Maximum wait in ms (default 10000)
     * @returns {Promise<object>} The ready peer
     */
    _waitForPeer(peerId, timeout = 30000) {
        return new Promise((resolve, reject) => {
            let currentPeer = null
            let replacements = 0
            const MAX_REPLACEMENTS = 3

            const timer = setTimeout(() => {
                cleanup()
                reject(new Error(`peer never became ready: ${peerId}`))
            }, timeout)

            const cleanup = () => {
                clearTimeout(timer)
                if (currentPeer) {
                    currentPeer.off('connect', onConnect)
                    currentPeer.off('close', onClose)
                }
            }

            const onConnect = () => {
                cleanup()
                resolve(this._peers[peerId])
            }

            const onClose = () => {
                const replacement = this._peers[peerId]
                if (replacement && replacement !== currentPeer && replacements < MAX_REPLACEMENTS) {
                    replacements++
                    currentPeer.off('connect', onConnect)
                    currentPeer.off('close', onClose)
                    watchPeer(replacement)
                    return
                }
                cleanup()
                reject(new Error(`peer closed before ready: ${peerId}`))
            }

            const watchPeer = (peer) => {
                currentPeer = peer
                if (peer.connected || peer._channel?.readyState === 'open') {
                    cleanup()
                    resolve(peer)
                    return
                }
                if (peer.destroyed || peer._destroyed) {
                    const replacement = this._peers[peerId]
                    if (replacement && replacement !== peer && replacements < MAX_REPLACEMENTS) {
                        replacements++
                        watchPeer(replacement)
                        return
                    }
                    cleanup()
                    reject(new Error(`peer already destroyed: ${peerId}`))
                    return
                }
                peer.on('connect', onConnect)
                peer.on('close', onClose)
            }

            const peer = this._peers[peerId]
            if (!peer) {
                clearTimeout(timer)
                return reject(new Error(`peer not found: ${peerId}`))
            }
            watchPeer(peer)
        })
    }

    /**
     * Wire up listeners on a newly created peer:
     * - ICE failure handling
     * - State sync listeners
     * - Event bus listeners
     * - Fire peerConnected event + deprecated global callbacks
     */
    _wirePeerListeners(peer, peerId) {
        // ICE failure → reconnect
        peer.on('iceFailure', () => this._handlePeerIceFailure(peerId))
        // A connection that opens ends the backoff
        peer.on('connect', () => this._connectBackoff.succeed(peerId))

        // Mirror group:peerMessage from dataBig into peerMessage event
        peer.on('dataBig', (data) => {
            if (data.type === 'group:peerMessage') {
                this.fire('peerMessage', { peerId, message: data.message, transport: 'webrtc' })
            }
        })

        // Event bus listeners
        this._setupEventBusListeners(peer, peerId)

        // Fire peerConnected for group-level listeners
        _publishNewPeer(peer) // deprecated global callback
        this.fire('peerConnected', peer)
    }

    /**
     * Destroy and remove a peer from this group's peer map.
     */
    _destroyPeer(peerId) {
        const peer = this._peers[peerId]
        if (peer) {
            delete this._peers[peerId]
            peer.destroy()
        }
    }

    /**
     * Diagnostic info about the current state leader.
     * Access from console: window.cameraMapGroup.leaderHealth or window.geoCameraGroup.leaderHealth
     */
    get leaderHealth() {
        const myId = acequia.getInstanceId()
        const leader = this._stateLeader
        const inPeersList = leader ? !!this.peersList[leader] : false
        const hasWebRTC = leader ? this._stateHasOpenConnection(leader) : false
        const now = Date.now()
        const lastResponse = this._lastLeaderResponse
        const silenceSec = lastResponse ? Math.round((now - lastResponse) / 1000) : null
        const peerIds = Object.keys(this.peersList)
        const candidates = Object.entries(this.peersList)
            .filter(([, info]) => info.capabilities?.includes('state-leader'))
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([id, info]) => ({
                id: id.slice(0, 8),
                fullId: id,
                displayName: info.displayName || info.deviceName || id.slice(0, 8),
                isLeader: id === leader,
                isSelf: id === myId,
            }))

        const health = {
            leader: leader?.slice(0, 8) || null,
            leaderFullId: leader,
            isSelf: leader === myId,
            inPeersList,
            hasWebRTC,
            lastResponseAgo: silenceSec !== null ? `${silenceSec}s` : 'never',
            lastResponseTs: lastResponse || null,
            isResponsive: lastResponse > 0 && (now - lastResponse) < 30000,
            stateKeys: Object.keys(this._state).length,
            stateVersion: this._stateVersion,
            totalPeers: peerIds.length,
            candidates,
            myId: myId?.slice(0, 8),
            myFullId: myId,
        }

        // Also log to console in a readable format
        const status = health.isSelf ? 'SELF'
            : !health.inPeersList ? 'NOT IN PEERS LIST'
            : !health.isResponsive ? `UNRESPONSIVE (silent ${health.lastResponseAgo})`
            : 'OK'
        console.log(`[leader-health] ${health.leader || 'none'} — ${status}`)
        console.log(`  peers: ${peerIds.map(id => `${id.slice(0, 8)}${id === leader ? '*' : ''}${id === myId ? '(me)' : ''}`).join(', ')}`)
        console.log(`  candidates: ${candidates.map(c => `${c.id}${c.isLeader ? '*' : ''}${c.isSelf ? '(me)' : ''} "${c.displayName}"`).join(', ')}`)
        console.log(`  state: ${health.stateKeys} keys, v${health.stateVersion}`)

        return health
    }

    _startLeaderHealthMonitor() {
        this._stopLeaderHealthMonitor()
        this._leaderHealthId = setInterval(() => {
            if (this._isStateLeader || !this._stateLeader) return
            const now = Date.now()
            const leader = this._stateLeader
            const inPeersList = !!this.peersList[leader]

            if (!inPeersList) {
                console.warn(`[leader-health] ⚠ leader ${leader.slice(0, 8)} NOT in peersList — ghost leader from stale discovery data`)
                return
            }

            const silence = this._lastLeaderResponse ? now - this._lastLeaderResponse : null
            if (silence !== null && silence > 30000) {
                if (this._maybeDeposeLeader(leader, now)) return
                console.warn(`[leader-health] leader ${leader.slice(0, 8)} unresponsive (${Math.round(silence / 1000)}s) — requesting resync`)
                this._unansweredResyncs++
                this._stateSendToPeer(leader, { type: 'groupState:resyncRequest' })
            } else if (silence === null && now - this._lastActiveTimestamp > 30000) {
                if (this._maybeDeposeLeader(leader, now)) return
                console.warn(`[leader-health] leader ${leader.slice(0, 8)} never responded since election — requesting resync`)
                this._unansweredResyncs++
                this._stateSendToPeer(leader, { type: 'groupState:resyncRequest' })
            }
        }, 15000)
    }

    /**
     * Depose a leader that pongs the server but stopped answering this page
     * (acequia/leaderDepose.js has the rules): the same capabilityDepose that
     * GroupExplorer's Depose button sends. The server drops it from the list
     * and rebroadcasts; the next capabilityList applies the new leader.
     * @returns {boolean} whether a depose was sent
     */
    _maybeDeposeLeader(leader, now) {
        const openChannelsElsewhere = Object.entries(this._peers).filter(([id, p]) =>
            id !== leader && !p._destroyed && p._channel?.readyState === 'open'
        ).length
        const verdict = shouldDeposeLeader({
            now,
            leader,
            self: acequia.getInstanceId(),
            members: this._serverCapabilityList,
            leaderSince: this._leaderSince,
            lastLeaderResponse: this._lastLeaderResponse,
            unansweredResyncs: this._unansweredResyncs,
            openChannelsElsewhere,
            lastDeposeAt: this._lastDeposeAt,
        })
        if (!verdict.depose) return false
        if (this.server?.readyState !== WebSocket.OPEN) return false
        console.warn(`[leader-health] deposing leader ${leader.slice(0, 8)}: silent ${Math.round(verdict.silentMs / 1000)}s, ${this._unansweredResyncs} resync requests unanswered, ${openChannelsElsewhere} open channels here`)
        this._lastDeposeAt = now
        this.server.send(JSON.stringify({
            type: 'capabilityDepose',
            capability: 'state-leader',
            targetInstanceId: leader,
        }))
        return true
    }

    _stopLeaderHealthMonitor() {
        if (this._leaderHealthId) {
            clearInterval(this._leaderHealthId)
            this._leaderHealthId = null
        }
    }

    /**
     * Connect to a peer by instanceId
     * @param {string} instanceId
     * @returns {Promise<object>} The peer connection
     */
    async connectToPeer(instanceId, { timeout } = {}) {
        const peerInfo = this.getPeerInfoByInstanceId(instanceId)
        if (!peerInfo) {
            throw new Error(`No peer info found for instance: ${instanceId}`)
        }

        // Reuse existing open connection from this group's peer map
        const existing = this._peers[instanceId]
        if (existing && !existing._destroyed && existing._channel?.readyState === 'open') {
            if (!existing.ready) existing.ready = Promise.resolve(existing)
            return existing
        }
        // A passive peer never offers; whoever wants it connects to it.
        if (this.config.passivePeer) {
            throw new Error(`passive peer: not initiating a connection to ${instanceId.slice(0, 8)}`)
        }

        // Check if already connecting
        if (this.pendingPeerRequests[instanceId]) {
            return this.pendingPeerRequests[instanceId]
        }

        // Create connection promise — assign synchronously before first await
        const connectionPromise = (async () => {
            try {
                const iceServers = await getIceServers()

                // During the async getIceServers(), an incoming offer may have
                // already created a responder peer for this peerId. Reuse it
                // instead of creating an initiator that would destroy it.
                // A peer that already had a full wait and never connected is
                // dead (its offer or answer went nowhere): replace it below
                // rather than wait on it again.
                const responder = this._peers[instanceId]
                const open = responder && (responder.connected || responder._channel?.readyState === 'open')
                if (responder && !responder._destroyed && (open || !responder._readyFailed)) {
                    if (debug) console.log(`[webrtc] ${instanceId.slice(0, 8)} responder already exists, skipping initiator`)
                    if (!responder.ready && open) responder.ready = Promise.resolve(responder)
                    return await this._readyFor(responder, instanceId, timeout)
                }

                // Clean up existing peer — but only if truly dead.
                // Peers in 'open' or 'connecting' state may be healthy
                // connections that survive a discovery server restart.
                const stale = this._peers[instanceId]
                if (stale) {
                    const chanState = stale._channel?.readyState
                    if (chanState === 'open' || (chanState === 'connecting' && !stale._readyFailed)) {
                        if (debug) console.log(`[webrtc] ${instanceId.slice(0, 8)} peer channel is ${chanState}, not destroying`)
                        return await this._readyFor(stale, instanceId, timeout)
                    }
                    if (debug) console.log(`[webrtc] ${instanceId.slice(0, 8)} replacing a peer that never connected (channel ${chanState ?? 'none'})`)
                    delete this._peers[instanceId]
                    stale.destroy()
                }

                const peer = createPeer({
                    peerId: instanceId,
                    group: this,
                    initiator: true,
                    trickle: true,
                    iceServers,
                    transport: this.transport,
                })
                this._peers[instanceId] = peer
                this._wirePeerListeners(peer, instanceId)

                return await this._readyFor(peer, instanceId, timeout)
            } finally {
                delete this.pendingPeerRequests[instanceId]
            }
        })()

        this.pendingPeerRequests[instanceId] = connectionPromise
        return connectionPromise
    }

    /**
     * Send a request via a peer and wait for response
     * @param {object} peer - The peer connection
     * @param {object} request - The request object
     * @param {object} [opts]
     * @param {string} [opts.groupId] - Override the groupId sent to the receiver
     * @returns {Promise<object>} The response
     */
    async sendRequestViaPeer(peer, request, opts) {
        // Message-level streams over the data channel
        // (browserdav-transport-robustness): a large body goes out as
        // peerRequestStream start/data/end messages (one sendBig per 512 KB
        // chunk — each rides the existing chunker + backpressure, so peak
        // wire memory is one chunk and sendBig's per-payload 30s ceiling
        // never applies to the whole file). Streamed responses arrive the
        // same way as peerResponseStream messages. Gated on the remote
        // peer's streamMessages advert (via peersList).
        const remoteStreams =
            this.peersList?.[peer.peerId || peer.id]?.streamMessages === true
        // Live mode: resolve at the response-stream 'start' with a
        // ReadableStream body fed by incoming chunk messages — true
        // time-to-first-byte for consumers that can use a stream (the
        // service worker builds its Response from it directly). Default is
        // buffered (assemble a Blob, resolve at 'end').
        const liveResponse = opts?.preferStreamBody === true
        const IDLE_TIMEOUT = 30000

        return new Promise((resolve, reject) => {
            let timeout
            let respController = null // live-mode stream controller
            const fail = (err) => {
                clearTimeout(timeout)
                peer.off('dataBig', responseHandler)
                respController?.error(err)
                respController = null
                reject(err) // no-op if already resolved (live mode)
            }
            const armIdle = () => {
                clearTimeout(timeout)
                timeout = setTimeout(() => {
                    console.warn(`[route-diag] Peer request timeout: requestId=${request.requestId}, path=${request.path}, peer=${(peer.peerId || peer.id)?.slice(0, 8)}, channel=${peer._channel?.readyState}`)
                    fail(new Error('Peer request timeout'))
                }, IDLE_TIMEOUT)
            }
            armIdle()

            // Streamed response assembly state
            let respMeta = null
            let respChunks = null
            let respSeq = 0

            const responseHandler = (data) => {
                try {
                    if (
                        data.type === 'peerProxiedResponse' &&
                        data.requestId === request.requestId
                    ) {
                        clearTimeout(timeout)
                        peer.off('dataBig', responseHandler)
                        resolve(data.response)
                    } else if (
                        data.type === 'peerResponseStream' &&
                        data.streamId === request.requestId
                    ) {
                        armIdle()
                        if (data.kind === 'start') {
                            respMeta = data.response
                            respSeq = data.seq
                            if (liveResponse) {
                                const bodyStream = new ReadableStream({
                                    start(controller) {
                                        respController = controller
                                    },
                                })
                                resolve({
                                    ...respMeta,
                                    data: { ...respMeta.data, body: bodyStream },
                                })
                            } else {
                                respChunks = []
                            }
                        } else if (!respMeta || data.seq !== respSeq + 1) {
                            fail(new Error('peer response stream sequence error'))
                        } else if (((respSeq = data.seq), data.kind === 'data')) {
                            const chunk =
                                data.chunk instanceof Uint8Array
                                    ? data.chunk
                                    : new Uint8Array(data.chunk)
                            if (respController) respController.enqueue(chunk)
                            else respChunks.push(chunk)
                        } else if (data.kind === 'end') {
                            clearTimeout(timeout)
                            peer.off('dataBig', responseHandler)
                            if (respController) {
                                respController.close()
                                respController = null
                            } else {
                                resolve({
                                    ...respMeta,
                                    data: { ...respMeta.data, body: new Blob(respChunks) },
                                })
                            }
                        } else if (data.kind === 'abort') {
                            fail(new Error(data.error || 'peer response stream aborted'))
                        }
                    }
                } catch (error) {
                    fail(error)
                }
            }

            peer.on('dataBig', responseHandler)

            // Convert ArrayBuffer body to Blob for better WebRTC compatibility
            const requestToSend = { ...request }
            if (requestToSend.body instanceof ArrayBuffer) {
                requestToSend.body = new Blob([requestToSend.body], {
                    type:
                        request.headers?.['content-type'] ||
                        'application/octet-stream',
                })
            }
            // Remove the original type field to avoid conflicts
            delete requestToSend.type
            // We can always assemble a streamed response
            requestToSend.acceptStreamedResponse = true

            const groupId = opts?.groupId || this.groupId
            // Stream gate: the peer's transport config (which the group's
            // seeded), falling back for peers/groups that predate it.
            const streamThreshold =
                peer.transport?.streamThreshold ??
                this.transport?.streamThreshold ??
                DEFAULT_TRANSPORT.streamThreshold
            const bigBody =
                remoteStreams &&
                typeof Blob !== 'undefined' &&
                requestToSend.body instanceof Blob &&
                requestToSend.body.size > streamThreshold

            if (!bigBody) {
                peer.sendBig({
                    type: 'peerProxiedRequest',
                    groupId,
                    ...requestToSend,
                })
                return
            }

            // Stream the request body out as chunk messages
            const body = requestToSend.body
            delete requestToSend.body
            ;(async () => {
                const CHUNK = 512 * 1024
                let seq = 0
                // A false sendBig (closed channel, drain/send timeout) must
                // abort the stream — see the catch below.
                const put = async (msg) => {
                    if ((await peer.sendBig(msg)) === false) {
                        throw new Error(`sendBig failed (kind=${msg.kind}, seq=${msg.seq})`)
                    }
                }
                await put({
                    type: 'peerRequestStream',
                    kind: 'start',
                    streamId: request.requestId,
                    seq,
                    groupId,
                    ...requestToSend,
                })
                let total = 0
                for (let off = 0; off < body.size; off += CHUNK) {
                    const chunk = new Uint8Array(
                        await body.slice(off, off + CHUNK).arrayBuffer()
                    )
                    total += chunk.byteLength
                    seq++
                    await put({
                        type: 'peerRequestStream',
                        kind: 'data',
                        streamId: request.requestId,
                        seq,
                        chunk,
                    })
                    armIdle() // sending progress keeps the request alive
                }
                seq++
                await put({
                    type: 'peerRequestStream',
                    kind: 'end',
                    streamId: request.requestId,
                    seq,
                    totalBytes: total,
                })
            })().catch((e) => {
                try {
                    peer.sendBig({
                        type: 'peerRequestStream',
                        kind: 'abort',
                        streamId: request.requestId,
                        error: String(e?.message || e),
                    })
                } catch { /* channel dead */ }
                fail(e)
            })
        })
    }

    // -----------------------------------------------------------------------
    // Shared State — peer-elected leader with WebRTC primary, WS fallback
    // -----------------------------------------------------------------------

    get state() { return this._state }

    setState(patch) {
        if (!patch || typeof patch !== 'object') return

        // A peer that WRITES group state must be able to HOST it. Leader
        // eligibility is opt-in via config.capabilities, so an app that patches
        // state without declaring 'state-leader' can end up in a group where
        // `candidates` is empty — not a failed election, but no election
        // possible — and every patch queues forever.
        //
        // Observed 2026-09-18 on BrowserDAV: capabilities ['webdav'],
        // candidates [], stateVersion 0, and `authRequired` (the flag deciding
        // whether that server is open) stuck unwritable in the queue. The
        // WorkerPool constructor already applies this principle for workers
        // ("Workers automatically participate in state leader election"); this
        // generalises it to anyone who writes, which is the set that actually
        // needs it.
        this._ensureStateLeaderCandidacy()

        // Optimistic local merge (deep)
        this._deepMerge(this._state, patch)

        this.fire('stateChanged', {
            state: this._state,
            patch,
            peerId: acequia.getInstanceId(),
            version: null, // local-only, not yet confirmed
        })

        // Send to leader
        this._stateSendToLeader({ type: 'groupState:patch', patch })
    }

    /**
     * Declare 'state-leader' if we have not, so this peer can be elected to
     * host the state it is writing. Idempotent and cheap.
     *
     * Pushing into `config.capabilities` is what makes this durable: the
     * post-ready join loop and the ping-timeout rejoin path both read from
     * there, so a peer that declares late still rejoins correctly after a
     * reconnect. When the socket is already open we also join immediately,
     * because the caller is mid-write and the patch is about to queue.
     */
    _ensureStateLeaderCandidacy() {
        if (!this.config.capabilities) this.config.capabilities = []
        if (this.config.capabilities.includes('state-leader')) return
        this.config.capabilities.push('state-leader')
        if (this.server?.readyState === WebSocket.OPEN) {
            this.server.send(
                JSON.stringify({
                    type: 'capabilityJoin',
                    capability: 'state-leader',
                    instanceId: this.getDeviceInfo().instanceId,
                })
            )
        }
        console.log(
            '[state] declared state-leader — a peer that writes group state must be able to host it'
        )
    }

    /**
     * Handle server-authoritative capability list update.
     * The server tracks join order and liveness — leader is first in list.
     */
    _handleCapabilityList(data) {
        this._rejoinIfDropped(data)
        if (data.capability !== 'state-leader') return
        const prevMemberCount = this._serverCapabilityList?.length ?? -1
        this._serverCapabilityList = data.members || []
        const newLeader = data.leader || null
        const myId = acequia.getInstanceId()
        const selfInList = this._serverCapabilityList.some(m => m.instanceId === myId)
        if (newLeader !== this._stateLeader || this._serverCapabilityList.length !== prevMemberCount || !selfInList) {
            console.log(`[state] capabilityList: leader=${newLeader?.slice(0, 8) || 'none'}, members=${data.members?.map(m => m.instanceId.slice(0, 8)).join(', ')}${selfInList ? '' : ' (self NOT in list)'}`)
        }

        this._applyLeader(newLeader)

        // Version heartbeat: if the leader's health includes stateVersion and
        // we're behind, request a resync. This catches silent divergence where
        // a peer missed broadcasts but has non-empty (stale) state.
        if (!this._isStateLeader && newLeader && newLeader !== myId) {
            const leaderMember = this._serverCapabilityList.find(m => m.instanceId === newLeader)
            const leaderVersion = leaderMember?.health?.stateVersion
            if (leaderVersion != null && this._stateVersion < leaderVersion) {
                console.log(`[state] behind leader: local v${this._stateVersion} < leader v${leaderVersion} — requesting resync`)
                this._stateSendToPeer(newLeader, { type: 'groupState:resyncRequest' })
            }
        }
    }

    /**
     * Back into a capability list the server dropped us from. The server
     * removes a member silent for 15 seconds (a frozen tab, a short sleep, a
     * long stall) and broadcasts the list without it; the socket may well
     * survive, so the rejoin on reconnect never comes. Any capability this
     * peer declared, not only state-leader: a role addressed by
     * `/@<capability>/…` has no leader while its only member is out.
     * The server puts us at the end of the list.
     */
    _rejoinIfDropped(data) {
        if (!data?.capability || !this.config.capabilities?.includes(data.capability)) return
        const myId = this.getDeviceInfo().instanceId
        if ((data.members || []).some(m => m.instanceId === myId)) return
        if (this.server?.readyState !== WebSocket.OPEN) return
        console.log(`[group] rejoining capability list "${data.capability}" (removed by server, likely ping timeout)`)
        this.server.send(JSON.stringify({
            type: 'capabilityJoin',
            capability: data.capability,
            instanceId: myId,
        }))
    }

    /**
     * When peersList changes, check if the current leader is still present.
     * If not, fall back to local election from cached capability list.
     */
    _onPeersListForLeader() {
        if (!this._stateLeader) return
        // If the current leader disappeared from peersList, re-derive from capability list
        if (!this.peersList[this._stateLeader]) {
            console.log(`[state] leader ${this._stateLeader.slice(0, 8)} left peersList — re-deriving from capability list`)
            const caps = this._serverCapabilityList || []
            const alive = caps.filter(m => this.peersList[m.instanceId])
            const newLeader = alive[0]?.instanceId || null
            this._applyLeader(newLeader)
        } else if (this._isStateLeader) {
            // We're leader and peers changed — connect to new members
            this._scheduleConnectToMembers()
        }
    }

    /**
     * Apply a new leader — shared by capabilityList handler and peersList fallback.
     */
    async _applyLeader(newLeader) {
        if (newLeader === this._stateLeader) {
            if (this._isStateLeader) this._scheduleConnectToMembers()
            return
        }

        // Cancel pending connections from previous election
        this._electionEpoch++
        this._wsRelayPeers.clear()
        this._stateSentPeers.clear()

        this._stateLeader = newLeader
        this._lastLeaderResponse = 0
        this._leaderSince = Date.now()
        this._unansweredResyncs = 0
        const myId = acequia.getInstanceId()
        const peerIds = Object.keys(this.peersList)
        const leaderDisplay = newLeader
            ? (this.peersList[newLeader]?.displayName || this.peersList[newLeader]?.deviceName || newLeader.slice(0, 8))
            : 'none'
        console.log(`[state] leader changed: ${newLeader?.slice(0, 8) || 'none'} "${leaderDisplay}" (${peerIds.length} peers: ${peerIds.map(id => `${id.slice(0, 8)}${id === myId ? '(me)' : ''}`).join(', ')})`)

        if (newLeader === myId) {
            this._stopLeaderHealthMonitor()
            this._onElectedLeader(this._electionEpoch)
        } else {
            this._isStateLeader = false
            this._startLeaderHealthMonitor()
            // Replay patches that were queued before leader was set
            if (this._leaderPendingPatches.length > 0 && newLeader) {
                const queued = this._leaderPendingPatches.splice(0)
                console.log(`[state] replaying ${queued.length} queued patches to leader ${newLeader.slice(0, 8)}`)
                for (const patch of queued) {
                    await this._stateSendToPeer(newLeader, { type: 'groupState:patch', patch })
                }
            }
            if (Object.keys(this._state).length === 0 && newLeader) {
                console.log(`[state] requesting state from leader ${newLeader.slice(0, 8)} (empty state after leader change)`)
                this._stateSendToPeer(newLeader, {
                    type: 'groupState:resyncRequest',
                })
                const retryEpoch = this._electionEpoch
                setTimeout(() => {
                    if (this._electionEpoch !== retryEpoch) return
                    if (Object.keys(this._state).length === 0 && this._stateLeader) {
                        console.log(`[state] retrying state request to leader ${this._stateLeader.slice(0, 8)}`)
                        this._stateSendToPeer(this._stateLeader, {
                            type: 'groupState:resyncRequest',
                        })
                    }
                }, 3000)
                setTimeout(() => {
                    if (this._electionEpoch !== retryEpoch) return
                    if (Object.keys(this._state).length === 0) {
                        const peers = Object.keys(this.peersList).filter(id => id !== myId)
                        console.log(`[state] leader unresponsive — requesting state from all ${peers.length} peers`)
                        this._fallbackRecoveryUntil = Date.now() + 10000
                        for (const peerId of peers) {
                            this._stateSendToPeer(peerId, {
                                type: 'groupState:request',
                            })
                        }
                    }
                }, 5000)
            }
        }
    }

    /** Fallback local election for servers that don't support capabilityList */
    _electLeader() {
        if (this._serverCapabilityList) return
        const candidates = Object.entries(this.peersList)
            .filter(([, info]) => info.capabilities?.includes('state-leader'))
            .sort(([a], [b]) => a.localeCompare(b))
        this._applyLeader(candidates[0]?.[0] || null)
    }

    async _onElectedLeader(epoch) {
        this._isStateLeader = true
        this._acceptingPatches = false

        // Collect state from other peers before broadcasting
        const peers = Object.keys(this.peersList)
            .filter(id => id !== acequia.getInstanceId())

        if (peers.length > 0) {
            const responses = []
            const requestMsg = { type: 'groupState:request' }
            for (const peerId of peers) {
                this._stateSendToPeer(peerId, requestMsg)
            }

            // Wait for responses (up to 15s, but exit early once majority respond)
            await new Promise(resolve => {
                let resolved = false
                const done = () => { if (!resolved) { resolved = true; resolve() } }
                const timeout = setTimeout(done, 15000)
                const majority = Math.ceil(peers.length / 2)
                let majorityTimeout = null
                this._stateCollector = (data) => {
                    responses.push(data)
                    if (responses.length >= peers.length) {
                        clearTimeout(timeout)
                        if (majorityTimeout) clearTimeout(majorityTimeout)
                        done()
                    } else if (responses.length >= majority && !majorityTimeout) {
                        // Majority responded — give remaining peers 2s more
                        majorityTimeout = setTimeout(done, 2000)
                    }
                }
            })
            this._stateCollector = null

            // Pick highest-versioned state; on tie, prefer most recently active
            let adoptedFrom = null
            const preVersion = this._stateVersion
            for (const resp of responses) {
                if (resp.version > this._stateVersion ||
                    (resp.version === this._stateVersion && resp.lastActive > (adoptedFrom?.lastActive || 0))) {
                    this._state = resp.state
                    this._stateVersion = resp.version
                    adoptedFrom = resp
                }
            }

            const gap = this._stateVersion - preVersion
            if (gap > 1000) {
                console.warn(`[state] WARNING: version gap of ${gap} detected (was ${preVersion}, now ${this._stateVersion}) — adopting highest version from peer`)
            }
            if (debug) console.log(`[state] collected state from ${responses.length} peers, version ${this._stateVersion}`)
        }

        // Check epoch — election may have changed during collection
        if (epoch !== this._electionEpoch) return

        // Process queued patches (from other peers during election)
        this._acceptingPatches = true
        for (const queued of this._patchQueue) {
            this._mergeState(queued.patch, queued.peerId)
        }
        this._patchQueue = []

        // Replay own patches queued before leader was set
        if (this._leaderPendingPatches.length > 0) {
            const pending = this._leaderPendingPatches.splice(0)
            console.log(`[state] replaying ${pending.length} own queued patches as leader`)
            for (const patch of pending) {
                this._mergeState(patch, acequia.getInstanceId())
            }
        }

        // Fire local event so UI updates with collected state
        if (Object.keys(this._state).length > 0) {
            this.fire('stateChanged', {
                state: this._state,
                patch: this._state,
                peerId: null,
                version: this._stateVersion,
            })
        }

        // Broadcast authoritative state and connect to members
        this._stateBroadcastFull()
        this._stateConnectToMembers(epoch)
    }

    _scheduleConnectToMembers() {
        clearTimeout(this._connectMembersTimer)
        const epoch = this._electionEpoch
        this._connectMembersTimer = setTimeout(() => {
            if (epoch === this._electionEpoch) this._stateConnectToMembers(epoch)
        }, 2000)
    }

    async _stateConnectToMembers(epoch) {
        const myId = acequia.getInstanceId()
        const fullStateMsg = () => ({
            type: 'groupState:changed',
            state: this._state,
            version: this._stateVersion,
            patch: null,
            peerId: null,
            fullState: true,
            ts: Date.now(),
        })

        for (const peerId of Object.keys(this.peersList)) {
            if (epoch !== this._electionEpoch) return
            if (peerId === myId) continue

            if (!this._stateSentPeers.has(peerId) && Object.keys(this._state).length > 0) {
                this.sendToPeer(peerId, fullStateMsg())
                this._stateSentPeers.add(peerId)
            }

            // Establish WebRTC for future messages (if not already connected)
            // Skip peers that already failed ICE and fell back to WS relay,
            // and respect ICE retry backoff timing
            const retryState = this._iceRetryState.get(peerId)
            if (retryState) {
                const elapsed = Date.now() - retryState.lastAttempt
                const backoff = 1000 * Math.pow(2, retryState.count)
                if (elapsed < backoff) continue  // still in backoff window
            }
            if (this.config.passivePeer) continue // answers offers, never makes one
            if (this._connectBackoff.blocked(peerId) || peerLimitActive()) continue
            if (!this._stateHasOpenConnection(peerId) && !this._connectingPeers.has(peerId) && !this._wsRelayPeers.has(peerId)) {
                this._connectingPeers.add(peerId)
                this.connectToPeer(peerId).then(() => {
                    this._wsRelayPeers.delete(peerId)
                }).catch(err => {
                    if (debug) console.warn(`[state] WebRTC to ${peerId.slice(0, 8)} failed:`, err.message)
                    if (err?.code !== PEER_LIMIT_CODE) this._connectBackoff.fail(peerId)
                    this._wsRelayPeers.add(peerId)
                    this._scheduleWsRelayPromotion()
                }).finally(() => {
                    this._connectingPeers.delete(peerId)
                })
            }
        }
    }

    /** The browser refused this peer's connection (its cap): the relay until promotion. */
    _onPeerLimit(peerId) {
        this._wsRelayPeers.add(peerId)
        this._scheduleWsRelayPromotion()
    }

    async _handlePeerIceFailure(peerId) {
        const shortId = peerId.slice(0, 8)

        // Peer creation is paused (the browser's cap): five restarts would each
        // be refused. The relay until promotion.
        if (peerLimitActive()) {
            this._destroyPeer(peerId)
            this._onPeerLimit(peerId)
            return
        }

        const MAX_RETRIES = 5
        const RETRY_WINDOW = 5 * 60 * 1000  // reset counter after 5 min of stability

        // Get or create retry state for this peer
        let retryState = this._iceRetryState.get(peerId) || { count: 0, lastAttempt: 0 }
        const now = Date.now()

        // Reset counter if outside retry window (peer was stable for a while)
        if (now - retryState.lastAttempt > RETRY_WINDOW) {
            retryState = { count: 0, lastAttempt: 0 }
        }

        if (retryState.count >= MAX_RETRIES) {
            console.warn(`[ice-restart] ${shortId} max retries (${MAX_RETRIES}) reached, falling back to WS relay`)
            this._wsRelayPeers.add(peerId)
            this._scheduleWsRelayPromotion()
            this._destroyPeer(peerId)
            return
        }

        const attempt = retryState.count
        console.log(`[ice-restart] ${shortId} attempting reconnect (attempt ${attempt + 1}/${MAX_RETRIES})`)

        // Update retry state
        retryState.count = attempt + 1
        retryState.lastAttempt = now
        this._iceRetryState.set(peerId, retryState)

        // Force-expire cached ICE credentials
        invalidateIceCache()

        // Destroy the failed peer
        this._destroyPeer(peerId)

        // Exponential backoff with jitter: ~1s → ~2s → ~4s → ~8s → ~16s
        const delay = 1000 * Math.pow(2, attempt) * (0.5 + Math.random() * 0.5)
        console.log(`[ice-restart] ${shortId} waiting ${Math.round(delay)}ms before retry`)
        await new Promise(r => setTimeout(r, delay))

        // Check peer is still in group (may have departed during backoff)
        if (!this.peersList[peerId]) {
            console.log(`[ice-restart] ${shortId} no longer in group, aborting`)
            this._iceRetryState.delete(peerId)
            return
        }

        // Reconnect via normal flow (fetches fresh TURN credentials)
        // iceFailure listener re-attached via _newPeerHandler
        try {
            await this.connectToPeer(peerId)
        } catch (err) {
            console.warn(`[ice-restart] ${shortId} reconnect failed, using WS relay:`, err.message)
            this._wsRelayPeers.add(peerId)
            this._scheduleWsRelayPromotion()
        }
    }

    _startZombieCheck() {
        if (this._zombieCheckTimer) return
        const INTERVAL = 60000
        const HIDDEN_INTERVAL = 300000
        const run = () => {
            const interval = (typeof document !== 'undefined' && document.hidden) ? HIDDEN_INTERVAL : INTERVAL
            this._zombieCheckTimer = setTimeout(async () => {
                if (this._destroyed) return
                await this._zombieSweep()
                run()
            }, interval)
        }
        run()
    }

    async _zombieSweep() {
        const peerIds = Object.keys(this._peers)
            .filter(id => !this._connectingPeers.has(id) && !this._wsRelayPeers.has(id))
        if (peerIds.length === 0) return

        // Stagger pings: 200ms apart to avoid burst
        for (let i = 0; i < peerIds.length; i++) {
            if (this._destroyed) return
            const peerId = peerIds[i]
            const peer = this._peers[peerId]
            if (!peer || peer._destroyed || peer._channel?.readyState !== 'open') continue

            if (i > 0) await new Promise(r => setTimeout(r, 200))

            try {
                await peer.sendPing({ timeout: 10000 })
            } catch {
                const shortId = peerId.slice(0, 8)
                console.warn(`[zombie] ${shortId} data channel ping timeout — treating as dead connection`)
                this._handlePeerIceFailure(peerId)
            }
        }
    }

    _startRouteHealthCheck() {
        if (this._routeHealthTimer) return
        const INTERVAL = 60000
        const run = () => {
            this._routeHealthTimer = setTimeout(() => {
                if (this._destroyed) return
                const localRoutes = this.router.getRouteRegistry()
                if (Object.keys(localRoutes).length === 0) { run(); return }
                if (this.server?.readyState === WebSocket.OPEN) {
                    this.server.send(JSON.stringify({ type: 'getRouteRegistry' }))
                }
                run()
            }, INTERVAL)
        }
        run()
    }

    _handleStatePatch(patch, peerId) {
        this._lastActiveTimestamp = Date.now()
        if (!this._isStateLeader) return

        if (!this._acceptingPatches) {
            console.log(`[state] queuing patch from ${peerId.slice(0, 8)} (election in progress)`)
            this._patchQueue.push({ patch, peerId })
            return
        }

        this._mergeState(patch, peerId)
    }

    _mergeState(patch, peerId) {
        const prevKeyCount = Object.keys(this._state).length
        const prevCameraCount = this._state.cameras ? Object.keys(this._state.cameras).length : 0
        this._deepMerge(this._state, patch)
        this._stateVersion++
        const keyCount = Object.keys(this._state).length
        const cameraCount = this._state.cameras ? Object.keys(this._state.cameras).length : 0
        if (keyCount !== prevKeyCount || cameraCount !== prevCameraCount) {
            console.log(`[state] merged patch from ${peerId.slice(0, 8)} → v${this._stateVersion} (${keyCount} keys, ${cameraCount} cameras)`)
        }

        // Broadcast to all peers
        this._stateBroadcastToAll({
            type: 'groupState:changed',
            state: this._state,
            version: this._stateVersion,
            patch,
            peerId,
            ts: Date.now(),
        })

        // Fire locally on the leader too
        this.fire('stateChanged', {
            state: this._state,
            patch,
            peerId,
            version: this._stateVersion,
        })
    }

    _handleStateChanged(data) {
        this._lastActiveTimestamp = Date.now()
        this._lastLeaderResponse = Date.now()
        this._unansweredResyncs = 0
        // Ignore if we're the leader (we're the authority)
        if (this._isStateLeader) {
            if (data.fullState) console.log(`[state] ignoring incoming full state (we are leader)`)
            return
        }
        const keyCount = Object.keys(data.state || {}).length
        const cameraCount = data.state?.cameras ? Object.keys(data.state.cameras).length : 0
        const snapshot = `${keyCount}:${cameraCount}:${!!data.fullState}`
        const versionGap = data.version - this._stateVersion
        if (snapshot !== this._lastLoggedStateSnapshot || data.fullState || versionGap > 1 || versionGap < 0) {
            console.log(`[state] received state: ${keyCount} keys, v${data.version}, fullState=${!!data.fullState}, cameras=${cameraCount}${versionGap > 1 ? ` (gap=${versionGap})` : ''}`)
            this._lastLoggedStateSnapshot = snapshot
        }

        // Full state broadcasts (resync after wake, leader election) always accepted
        // — bypass the stale version guard since these are authoritative.
        if (!data.fullState) {
            // Drop stale versions — leader already has newer state, no need to reconcile.
            // Previously this sent this._state (full state) as a patch, which overwrote
            // newer values from other peers when messages arrived out of order
            // (e.g. WebRTC vs WS relay transport switching during ICE reconnects).
            if (data.version !== null && data.version < this._stateVersion) {
                if (debug) console.log(`[state] dropping stale version ${data.version} (have ${this._stateVersion})`)
                return
            }
        }

        this._state = data.state
        this._stateVersion = data.version || this._stateVersion
        this.fire('stateChanged', {
            state: data.state,
            patch: data.patch,
            peerId: data.peerId,
            version: data.version,
        })
    }

    _handleStateRequest(fromPeerId) {
        this._stateSendToPeer(fromPeerId, {
            type: 'groupState:response',
            state: this._state,
            version: this._stateVersion,
            lastActive: this._lastActiveTimestamp,
        })
    }

    _handleResyncRequest(fromPeerId) {
        if (!this._isStateLeader) return
        console.log(`[state] resync request from ${fromPeerId.slice(0, 8)} — sending full state (${Object.keys(this._state).length} keys, v${this._stateVersion})`)
        this._stateSendToPeer(fromPeerId, {
            type: 'groupState:changed',
            state: this._state,
            version: this._stateVersion,
            patch: null,
            peerId: null,
            fullState: true,
            ts: Date.now(),
        })
    }

    _stateBroadcastFull() {
        this._stateBroadcastToAll({
            type: 'groupState:changed',
            state: this._state,
            version: this._stateVersion,
            patch: null,
            peerId: null,
            fullState: true,
            ts: Date.now(),
        })
    }

    _stateSendToLeader(message) {
        if (this._isStateLeader) {
            // We are the leader — handle locally
            if (message.type === 'groupState:patch') {
                this._handleStatePatch(message.patch, acequia.getInstanceId())
            }
            return
        }

        if (!this._stateLeader) {
            if (message.type === 'groupState:patch' && message.patch) {
                this._leaderPendingPatches.push(message.patch)
                console.warn(`[state] no leader — patch queued (keys: ${Object.keys(message.patch).join(', ')}, queue=${this._leaderPendingPatches.length})`)
            }
            return
        }
        this._stateSendToPeer(this._stateLeader, message)
    }

    async _stateSendToPeer(peerId, message) {
        const transport = await this.sendToPeer(peerId, message)
        if (debug) console.log(`[state] sent ${message.type} to ${peerId.slice(0, 8)} via ${transport}`)
    }

    _stateBroadcastToAll(message) {
        const myId = acequia.getInstanceId()
        for (const peerId of Object.keys(this.peersList)) {
            if (peerId === myId) continue
            this._stateSendToPeer(peerId, message)
        }
    }

    _stateHasOpenConnection(peerId) {
        const peer = this._peers[peerId]
        const open = peer && !peer._destroyed && peer._channel?.readyState === 'open'
        // Recover from WS relay if WebRTC has since connected
        if (open && this._wsRelayPeers.has(peerId)) {
            if (debug) console.log(`[state] ${peerId.slice(0, 8)} WebRTC recovered, clearing WS relay flag`)
            this._wsRelayPeers.delete(peerId)
        }
        return open
    }

    _scheduleWsRelayPromotion() {
        if (this._wsRelayPromotionTimer) return
        this._wsRelayPromotionTimer = setTimeout(() => {
            this._wsRelayPromotionTimer = null
            this._attemptWsRelayPromotion()
        }, 5 * 60 * 1000)
    }

    async _attemptWsRelayPromotion() {
        if (this._destroyed || this._wsRelayPeers.size === 0) return
        const candidates = [...this._wsRelayPeers].filter(id => this.peersList[id])
        if (candidates.length === 0) return
        if (peerLimitActive()) { this._scheduleWsRelayPromotion(); return }
        console.log(`[ws-relay] attempting WebRTC promotion for ${candidates.length} relay peer(s)`)
        for (const peerId of candidates) {
            const existing = this._peers[peerId]
            if (existing && !existing._destroyed && existing._channel?.readyState === 'open') {
                console.log(`[ws-relay] ${peerId.slice(0, 8)} already has open channel, promoting`)
                this._wsRelayPeers.delete(peerId)
                this._iceRetryState.delete(peerId)
                continue
            }
            if (this._connectingPeers.has(peerId)) continue
            this._iceRetryState.delete(peerId)
            this._connectingPeers.add(peerId)
            try {
                await this.connectToPeer(peerId)
                console.log(`[ws-relay] ${peerId.slice(0, 8)} promoted to WebRTC`)
                this._wsRelayPeers.delete(peerId)
            } catch (err) {
                console.log(`[ws-relay] ${peerId.slice(0, 8)} promotion failed: ${err.message}`)
            } finally {
                this._connectingPeers.delete(peerId)
            }
        }
        if (this._wsRelayPeers.size > 0) {
            this._scheduleWsRelayPromotion()
        }
    }

    _deepMerge(target, patch) {
        for (const key of Object.keys(patch)) {
            const value = patch[key]
            if (value === null || value === undefined) {
                delete target[key]
            } else if (
                typeof value === 'object' && !Array.isArray(value) &&
                typeof target[key] === 'object' && !Array.isArray(target[key]) &&
                target[key] !== null
            ) {
                this._deepMerge(target[key], value)
            } else {
                target[key] = value
            }
        }
        return target
    }

    // -------------------------------------------------------------------
    // Event Bus — Decentralized Pub/Sub
    // -------------------------------------------------------------------

    /**
     * Subscribe to events matching a topic pattern.
     * Returns an unsubscribe function.
     *
     *   const unsub = group.subscribe('file:*', (event) => { ... })
     *   unsub()  // stop receiving
     */
    subscribe(pattern, callback) {
        if (typeof pattern !== 'string' || typeof callback !== 'function') return () => {}

        let callbacks = this._localSubscriptions.get(pattern)
        const isNew = !callbacks
        if (!callbacks) {
            callbacks = new Set()
            this._localSubscriptions.set(pattern, callbacks)
        }
        callbacks.add(callback)

        // Advertise new pattern to all peers and establish WebRTC connections
        if (isNew) {
            this._ebBroadcastControl({ type: 'eventBus:sub', topics: [pattern] })
            this._eventBusConnectToSubscribers()
        }

        // Replay matching retained events to the new callback
        for (const [topic, event] of this._retainedEvents) {
            if (topicMatches(pattern, topic)) {
                try { callback({ ...event, retained: true, local: true }) } catch {}
            }
        }

        // Return unsubscribe function
        return () => {
            callbacks.delete(callback)
            if (callbacks.size === 0) {
                this._localSubscriptions.delete(pattern)
                this._ebBroadcastControl({ type: 'eventBus:unsub', topics: [pattern] })
            }
        }
    }

    /**
     * Publish an event to all peers with matching subscriptions.
     *
     *   group.publish('file:changed', { path: '/photos/cat.jpg' })
     *   group.publish('status:battery', { level: 0.42 }, { retain: true, ttl: 60000 })
     */
    publish(topic, data, opts) {
        const event = {
            type: 'eventBus:event',
            topic,
            data,
            peerId: acequia.getInstanceId(),
            msgId: crypto.randomUUID(),
            ts: Date.now(),
            retain: opts?.retain || false,
            ttl: opts?.ttl || null,
        }

        // Store retained event
        if (event.retain) {
            this._retainEvent(topic, event)
        }

        // Fire local subscriptions synchronously
        this._ebFireLocal(event, true)

        // Send to remote peers with matching subscriptions
        const myId = acequia.getInstanceId()
        for (const [peerId, patterns] of this._remoteSubscriptions) {
            if (peerId === myId) continue
            for (const pat of patterns) {
                if (topicMatches(pat, topic)) {
                    this._ebPublishToPeer(peerId, event)
                    break  // send once per peer
                }
            }
        }
    }

    /**
     * Read-only: local subscription patterns.
     */
    get subscriptions() {
        return [...this._localSubscriptions.keys()]
    }

    /**
     * Count of remote peers subscribed to a topic.
     */
    subscriberCount(topic) {
        let count = 0
        for (const [, patterns] of this._remoteSubscriptions) {
            for (const pat of patterns) {
                if (topicMatches(pat, topic)) { count++; break }
            }
        }
        return count
    }

    /** Fire matching local subscription callbacks for an event. */
    _ebFireLocal(event, local) {
        for (const [pattern, callbacks] of this._localSubscriptions) {
            if (topicMatches(pattern, event.topic)) {
                const delivered = { ...event, local }
                for (const cb of callbacks) {
                    try { cb(delivered) } catch (err) {
                        console.error(`[eventBus] subscriber error on ${pattern}:`, err)
                    }
                }
            }
        }
    }

    /** Handle an incoming event from a remote peer. */
    _handleIncomingEvent(event) {
        if (this._ebIsDuplicate(event.msgId)) return
        this._ebFireLocal(event, false)
    }

    /** Bounded seen-set for deduplication. */
    _ebIsDuplicate(msgId) {
        if (this._seenEvents.has(msgId)) return true
        this._seenEvents.add(msgId)
        this._seenEventsQueue.push(msgId)
        if (this._seenEventsQueue.length > 1000) {
            this._seenEvents.delete(this._seenEventsQueue.shift())
        }
        return false
    }

    /** Send an event to a single peer via WebRTC or WS relay. */
    _ebPublishToPeer(peerId, message) {
        if (!this._ebWsRelayPeers.has(peerId) && this._ebHasOpenConnection(peerId)) {
            this._peers[peerId].sendBig(message)
        } else {
            this.serverSendToPeer(peerId, message)
        }
    }

    /** Check if a WebRTC connection to a peer is open. */
    _ebHasOpenConnection(peerId) {
        const peer = this._peers[peerId]
        return peer && !peer._destroyed && peer._channel?.readyState === 'open'
    }

    /** Broadcast a control message to all connected peers. */
    _ebBroadcastControl(message) {
        const myId = acequia.getInstanceId()
        for (const peerId of Object.keys(this.peersList)) {
            if (peerId === myId) continue
            this._ebPublishToPeer(peerId, message)
        }
    }

    // ------- Event Bus: Subscription Advertisement -------

    _handleRemoteSub(peerId, topics) {
        let set = this._remoteSubscriptions.get(peerId)
        if (!set) {
            set = new Set()
            this._remoteSubscriptions.set(peerId, set)
        }
        for (const t of topics) set.add(t)
        this._replayRetainedFor(peerId, topics)
    }

    _handleRemoteUnsub(peerId, topics) {
        const set = this._remoteSubscriptions.get(peerId)
        if (!set) return
        for (const t of topics) set.delete(t)
        if (set.size === 0) this._remoteSubscriptions.delete(peerId)
    }

    _handleRemoteSync(peerId, topics) {
        this._remoteSubscriptions.set(peerId, new Set(topics))
        this._replayRetainedFor(peerId, topics)
    }

    // ------- Event Bus: Transport (connect-then-fallback) -------

    async _eventBusConnectToSubscribers() {
        const myId = acequia.getInstanceId()
        const myTopics = [...this._localSubscriptions.keys()]
        for (const peerId of Object.keys(this.peersList)) {
            if (peerId === myId) continue

            // If already connected via mesh, sync subscriptions but don't reconnect
            if (this._ebHasOpenConnection(peerId)) {
                try {
                    this._peers[peerId].sendBig({
                        type: 'eventBus:syncSubs',
                        topics: myTopics,
                    })
                } catch {}
                continue
            }
            if (this._connectingPeers.has(peerId)) {
                // Another connection attempt is in progress (e.g. state sync).
                // Still send subscriptions via WS relay so the peer knows what we listen to.
                this.serverSendToPeer(peerId, {
                    type: 'eventBus:syncSubs',
                    topics: myTopics,
                })
                continue
            }
            // Not now: an answer-only peer, a peer backing off after a failure,
            // one on the relay until its promotion, or peer creation paused.
            // This ran on every peersList change and retried every peer
            // without an open channel (connectBackoff.js).
            if (this.config.passivePeer || this._connectBackoff.blocked(peerId) || this._wsRelayPeers.has(peerId) || peerLimitActive()) {
                // An answer-only peer with no subscriptions has nothing to tell
                if (this.config.passivePeer && myTopics.length === 0) continue
                this.serverSendToPeer(peerId, {
                    type: 'eventBus:syncSubs',
                    topics: myTopics,
                })
                continue
            }

            this._connectingPeers.add(peerId)
            try {
                const peer = await this.connectToPeer(peerId)
                this._ebWsRelayPeers.delete(peerId)

                // Exchange subscription lists
                if (peer._channel?.readyState === 'open') {
                    try {
                        peer.sendBig({
                            type: 'eventBus:syncSubs',
                            topics: [...this._localSubscriptions.keys()],
                        })
                    } catch (e) {
                        if (debug) console.warn(`[eventBus] syncSubs send failed for ${peerId.slice(0, 8)}:`, e.message)
                    }
                } else {
                    // Channel not open yet — fall back to WS relay
                    this.serverSendToPeer(peerId, {
                        type: 'eventBus:syncSubs',
                        topics: [...this._localSubscriptions.keys()],
                    })
                }

                // Replay retained events for the remote peer
                this._replayRetainedFor(peerId)
            } catch (err) {
                if (debug) console.warn(`[eventBus] WebRTC to ${peerId.slice(0, 8)} failed, using WS relay`)
                if (err?.code !== PEER_LIMIT_CODE) this._connectBackoff.fail(peerId)
                this._ebWsRelayPeers.add(peerId)

                // Still exchange subscriptions via WS relay
                this.serverSendToPeer(peerId, {
                    type: 'eventBus:syncSubs',
                    topics: [...this._localSubscriptions.keys()],
                })
            } finally {
                this._connectingPeers.delete(peerId)
            }
        }
    }

    /** Set up event bus listeners on a WebRTC peer connection. */
    _setupEventBusListeners(peer, peerId) {
        const handler = (data) => {
            if (data.type === 'eventBus:event') this._handleIncomingEvent(data)
            else if (data.type === 'eventBus:sub') this._handleRemoteSub(peerId, data.topics)
            else if (data.type === 'eventBus:unsub') this._handleRemoteUnsub(peerId, data.topics)
            else if (data.type === 'eventBus:syncSubs') this._handleRemoteSync(peerId, data.topics)
        }
        peer.on('dataBig', handler)
        peer.on('close', () => peer.off('dataBig', handler))
    }

    // ------- Event Bus: Retained Events -------

    _retainEvent(topic, event) {
        // Clear previous TTL timer for this topic
        const prevTimer = this._retainedTimers.get(topic)
        if (prevTimer) clearTimeout(prevTimer)

        this._retainedEvents.set(topic, event)

        if (event.ttl) {
            const timerId = setTimeout(() => {
                // Only delete if the retained event is still the same one
                if (this._retainedEvents.get(topic)?.msgId === event.msgId) {
                    this._retainedEvents.delete(topic)
                }
                this._retainedTimers.delete(topic)
            }, event.ttl)
            this._retainedTimers.set(topic, timerId)
        }
    }

    /**
     * Replay retained events matching the given topics (or all remote subs for the peer).
     */
    _replayRetainedFor(peerId, topics) {
        if (!topics) {
            const set = this._remoteSubscriptions.get(peerId)
            if (!set) return
            topics = [...set]
        }
        for (const [topic, event] of this._retainedEvents) {
            if (topics.some((pat) => topicMatches(pat, topic))) {
                this._ebPublishToPeer(peerId, event)
            }
        }
    }

    destroy() {
        if (this._destroyed) return
        this._destroyed = true
        this.connected = false
        clearTimeout(this._wsConnectTimer)
        clearTimeout(this._zombieCheckTimer)
        this._zombieCheckTimer = null
        clearTimeout(this._routeHealthTimer)
        this._routeHealthTimer = null

        this.fire('destroyed')

        // Leave capability lists on the server
        if (this.server?.readyState === WebSocket.OPEN) {
            const caps = this.config.capabilities || []
            for (const cap of caps) {
                this.server.send(JSON.stringify({
                    type: 'capabilityLeave',
                    capability: cap,
                    instanceId: acequia.getInstanceId(),
                }))
            }
        }

        // Unregister from global registry
        acequia.unregisterGroup(this.groupId)

        // Update service worker about group membership change
        this.updateServiceWorkerGroups()

        // Clean up event bus state
        this._localSubscriptions.clear()
        this._remoteSubscriptions.clear()
        this._retainedEvents.clear()
        this._seenEvents.clear()
        this._seenEventsQueue.length = 0
        this._ebWsRelayPeers.clear()
        // _ebConnectingPeers removed — unified into _connectingPeers
        for (const timerId of this._retainedTimers.values()) clearTimeout(timerId)
        this._retainedTimers.clear()

        // Clean up lifecycle listeners
        if (this._visibilityHandler && typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this._visibilityHandler)
            this._visibilityHandler = null
        }
        if (typeof window !== 'undefined') {
            if (this._onlineHandler) window.removeEventListener('online', this._onlineHandler)
            if (this._resumeHandler) document.removeEventListener('resume', this._resumeHandler)
        }
        this._onlineHandler = null
        this._resumeHandler = null
        if (this._wsReconnectStableTimer) {
            clearTimeout(this._wsReconnectStableTimer)
            this._wsReconnectStableTimer = null
        }

        // Clean up shared state
        if (this._wsRelayPromotionTimer) {
            clearTimeout(this._wsRelayPromotionTimer)
            this._wsRelayPromotionTimer = null
        }
        this._wsRelayPeers.clear()
        this._connectingPeers.clear()
        this._patchQueue.length = 0
        this._leaderPendingPatches.length = 0
        this._stateCollector = null
        this._state = {}

        // Destroy all group-owned peers
        for (const [peerId, peer] of Object.entries(this._peers)) {
            delete this._peers[peerId]
            peer.destroy()
        }

        // Clean up routes
        this.router.routeRegistry = {}

        // Clean up WebSocket connection
        if (this.server) {
            this.server.close()
            this.server = null
        }

        // Clear heartbeat
        if (this.heartbeatTimeoutId) {
            clearTimeout(this.heartbeatTimeoutId)
            this.heartbeatTimeoutId = null
        }

        // Clear leader health monitor
        this._stopLeaderHealthMonitor()
    }

    /**
     * Close this group connection and clean up all resources.
     * Public API — alias for destroy().
     */
    close() {
        this.destroy()
    }

    /**
     * Returns a promise that resolves when the group is connected.
     * If already connected, resolves immediately. If not, resolves
     * on the next 'welcome' event (i.e., after reconnection).
     *
     * Unlike `group.ready` (which resolves once on first connection
     * and never resets), this method is safe to call after a
     * disconnect/reconnect cycle.
     *
     * @returns {Promise<void>}
     */
    whenConnected() {
        if (this.connected) return Promise.resolve()
        return new Promise(resolve => this.once('welcome', resolve))
    }
}

// ---------------------------------------------------------------------------
// Dispatch Strategies
// ---------------------------------------------------------------------------

const dispatchers = {
    'round-robin': (workers, job, state) => {
        const ids = Array.from(workers.keys()).sort()
        if (ids.length === 0) return null
        state.rrIndex = ((state.rrIndex || 0) % ids.length)
        const pick = ids[state.rrIndex]
        state.rrIndex = (state.rrIndex + 1) % ids.length
        return pick
    },

    'random': (workers, job, _state) => {
        const ids = Array.from(workers.keys())
        if (ids.length === 0) return null
        return ids[Math.floor(Math.random() * ids.length)]
    },

    'least-loaded': (workers, job, state) => {
        let best = null
        let bestRatio = Infinity
        for (const [id, info] of workers) {
            const pool = info.pool || {}
            const concurrency = pool.concurrency || 1
            const active = pool.activeJobs || 0
            const ratio = active / concurrency
            if (ratio < bestRatio) {
                bestRatio = ratio
                best = id
            }
        }
        // Fall back to round-robin on ties
        if (best === null) return dispatchers['round-robin'](workers, job, state)
        return best
    },

    'affinity': (workers, job, state) => {
        state.affinityMap = state.affinityMap || {}
        const lastWorker = state.affinityMap[job.type]
        if (lastWorker && workers.has(lastWorker)) {
            const info = workers.get(lastWorker)
            const pool = info.pool || {}
            const active = pool.activeJobs || 0
            const concurrency = pool.concurrency || 1
            if (active < concurrency) return lastWorker
        }
        return dispatchers['least-loaded'](workers, job, state)
    },
}

// ---------------------------------------------------------------------------
// JobTicket
// ---------------------------------------------------------------------------

/**
 * Tracks a submitted job from the submitter's perspective.
 * The `.result` promise resolves on success, rejects on failure/timeout.
 */
export class JobTicket {
    constructor(id, job) {
        this.id = id
        this.job = job
        this.status = 'pending'
        this.worker = null

        this._completeCallbacks = []
        this._failedCallbacks = []
        this._progressCallbacks = []
        this._timeoutCallbacks = []

        this._timeoutId = null
        this._retriesLeft = job.retries || 0
        this._retryOnDisconnect = job.retryOnDisconnect !== false
        this._failedWorkers = new Set()

        this.result = new Promise((resolve, reject) => {
            this._resolve = resolve
            this._reject = reject
        })
    }

    onComplete(fn) { this._completeCallbacks.push(fn) }
    onFailed(fn) { this._failedCallbacks.push(fn) }
    onProgress(fn) { this._progressCallbacks.push(fn) }
    onTimeout(fn) { this._timeoutCallbacks.push(fn) }

    cancel() {
        if (this.status === 'completed' || this.status === 'failed') return
        this._setStatus('failed')
        this._clearTimeout()
        this._reject(new Error('Job cancelled'))
    }

    _setStatus(status) {
        this.status = status
    }

    _complete(result) {
        this._setStatus('completed')
        this._clearTimeout()
        this._resolve(result)
        for (const fn of this._completeCallbacks) fn(result)
    }

    _fail(error) {
        this._setStatus('failed')
        this._clearTimeout()
        this._reject(error)
        for (const fn of this._failedCallbacks) fn(error)
    }

    _progress(data) {
        for (const fn of this._progressCallbacks) fn(data)
    }

    _startTimeout(ms) {
        this._clearTimeout()
        this._timeoutId = setTimeout(() => {
            if (this.status !== 'completed' && this.status !== 'failed') {
                this._setStatus('timed_out')
                this._reject(new Error(`Job timed out after ${ms}ms`))
                for (const fn of this._timeoutCallbacks) fn(this)
                for (const fn of this._failedCallbacks) fn(new Error('Job timed out'))
            }
        }, ms)
    }

    _clearTimeout() {
        if (this._timeoutId) {
            clearTimeout(this._timeoutId)
            this._timeoutId = null
        }
    }
}

// ---------------------------------------------------------------------------
// WorkerPool
// ---------------------------------------------------------------------------

/**
 * Distributed job queue over an Acequia Group.
 *
 * Submitter-side dispatch: each submitter picks a worker directly from
 * the peer list. No coordinator, no leader election.
 *
 * @param {string|Group} groupIdOrGroup — group ID (creates a new Group)
 *        or an existing Group instance (used as-is, for testing).
 * @param {object} options
 * @param {string[]} options.capabilities — job types this worker handles
 * @param {number} options.concurrency — max simultaneous jobs (default 1)
 * @param {Function} options.handler — async (job, ctx) => result
 * @param {string|Function} options.dispatch — strategy name or custom function
 * @param {string} options.displayName — peer display name
 */
export class WorkerPool {
    constructor(groupIdOrGroup, options = {}) {
        this._options = options
        this._handler = options.handler || null
        this._concurrency = options.concurrency != null ? options.concurrency : 1
        this._dispatchStrategy = options.dispatch || 'round-robin'
        this._dispatchState = {}
        this._closed = false
        this._ownsGroup = false

        // Workers map: instanceId → peer deviceInfo (only capable peers)
        this._workers = new Map()

        // Job tracking
        this._tickets = new Map()   // jobId → JobTicket
        this._activeJobs = 0        // this worker's active count
        this._completedJobs = 0     // this worker's completed count
        this._failedJobs = 0
        this._rejectedJobs = 0
        this._startedAt = Date.now()

        // Duration tracking (rolling window for avg/p95)
        this._durations = []        // last N job durations in ms
        this._maxDurations = 100    // rolling window size

        // Debounced stat update
        this._statUpdateTimer = null
        this._statUpdateInterval = 5000  // batch stats every 5s

        // Per-type stats tracking
        this._byType = {}  // jobType → { completed, failed, durations[] }

        // Observer tracking
        this._observers = new Set()     // instanceIds with pool-observer capability
        this._jobEventCallbacks = []    // onJobEvent listeners (observer side)

        // Peer connection cache — reuse WebRTC connections across dispatches
        this._peerCache = new Map()     // workerId → peer

        // Event callbacks
        this._workerJoinCallbacks = []
        this._workerLeaveCallbacks = []

        // Create or wrap group
        // Workers automatically participate in state leader election
        const caps = [...(options.capabilities || [])]
        if (this._handler && !caps.includes('state-leader')) {
            caps.push('state-leader')
        }

        if (typeof groupIdOrGroup === 'string') {
            this._ownsGroup = true
            this._group = new Group(groupIdOrGroup, {
                capabilities: caps,
                displayName: options.displayName,
                onPeersList: (peers) => this._onPeersChanged(peers),
            })
            this._group.ready.then(() => this._updateDeviceInfo(true))
        } else {
            // Existing group instance (e.g., MockGroup for testing)
            this._group = groupIdOrGroup
            this._group.on('peersList', (peers) => this._onPeersChanged(peers))
        }

        // Worker side: listen for incoming job offers via proxied requests
        if (this._handler) {
            this._setupWorkerListener()
        }

        // Observer side: set up event listener for pool:event messages
        this._setupEventListener()

        // Broadcast initial pool state
        this._updateDeviceInfo(true)
    }

    // --- Public API ---

    get group() { return this._group }
    get workers() { return new Map(this._workers) }
    get activeJobs() { return this._activeJobs }
    get completedJobs() { return this._completedJobs }

    onWorkerJoin(fn) { this._workerJoinCallbacks.push(fn) }
    onWorkerLeave(fn) { this._workerLeaveCallbacks.push(fn) }

    /**
     * Register a listener for job lifecycle events (observer side).
     * Events: job:dispatched, job:started, job:completed, job:failed, job:rejected, job:timeout
     */
    onJobEvent(fn) { this._jobEventCallbacks.push(fn) }

    /**
     * Submit a job to the pool.
     * @param {object} job — { type, payload, timeout, retries, retryOnDisconnect }
     * @returns {JobTicket}
     */
    submit(job) {
        if (this._closed) throw new Error('Pool is closed')
        if (!job.type) throw new Error('Job must have a type')

        const id = crypto.randomUUID()
        const ticket = new JobTicket(id, {
            timeout: 30000,
            retries: 0,
            retryOnDisconnect: true,
            ...job,
        })
        this._tickets.set(id, ticket)

        // Dispatch asynchronously
        this._dispatchTicket(ticket)

        return ticket
    }

    /**
     * Retrieve a ticket by ID (for status polling in bridged routes).
     */
    getTicket(id) {
        return this._tickets.get(id) || null
    }

    /**
     * Stop accepting new jobs, wait for active ones, then clean up.
     */
    async close() {
        this._closed = true

        // Wait for active jobs to finish (with a reasonable timeout)
        if (this._activeJobs > 0) {
            await Promise.race([
                new Promise(resolve => {
                    const check = () => {
                        if (this._activeJobs <= 0) resolve()
                        else setTimeout(check, 50)
                    }
                    check()
                }),
                new Promise(resolve => setTimeout(resolve, 5000)),
            ])
        }

        if (this._ownsGroup) {
            this._group.destroy()
        }
    }

    // --- Internal: dispatch ---

    async _dispatchTicket(ticket, excludeWorkers = null) {
        const capable = this._getCapableWorkers(ticket.job.type, excludeWorkers)
        if (capable.size === 0) {
            ticket._fail(new Error(`No workers available for job type: ${ticket.job.type}`))
            return
        }

        const strategy = typeof this._dispatchStrategy === 'function'
            ? this._dispatchStrategy
            : dispatchers[this._dispatchStrategy] || dispatchers['round-robin']

        const workerId = strategy(capable, ticket.job, this._dispatchState)
        if (!workerId) {
            ticket._fail(new Error(`No workers available for job type: ${ticket.job.type}`))
            return
        }

        ticket.worker = workerId
        ticket._setStatus('offered')

        const myInstanceId = this._group.getDeviceInfo?.()?.instanceId || this._group._instanceId

        this._emitJobEvent('job:dispatched', {
            jobId: ticket.id,
            jobType: ticket.job.type,
            worker: workerId,
            submitter: myInstanceId,
        })

        // Self-dispatch: run the handler locally instead of via WebRTC
        if (workerId === myInstanceId) {
            return this._dispatchLocal(ticket)
        }

        let progressHandler = null
        let peer = null
        const cleanupProgress = () => {
            if (progressHandler && peer) {
                peer.off('dataBig', progressHandler)
                progressHandler = null
            }
        }

        try {
            peer = await this._getOrConnectPeer(workerId)

            // Listen for progress messages on the data channel
            progressHandler = (data) => {
                if (data.type === 'pool:progress' && data.jobId === ticket.id) {
                    ticket._progress(data.data)
                }
            }
            peer.on('dataBig', progressHandler)

            // Start timeout
            ticket.onTimeout(() => {
                cleanupProgress()
                this._emitJobEvent('job:timeout', {
                    jobId: ticket.id, jobType: ticket.job.type,
                    worker: workerId,
                })
            })
            ticket._startTimeout(ticket.job.timeout)

            const requestId = crypto.randomUUID()
            const response = await this._group.sendRequestViaPeer(peer, {
                requestId,
                type: 'pool:job-offer',
                jobId: ticket.id,
                jobType: ticket.job.type,
                payload: ticket.job.payload,
                timeout: ticket.job.timeout,
            })

            cleanupProgress()

            if (response.type === 'pool:job-reject') {
                // Rejection doesn't count as a retry — try another worker
                ticket._failedWorkers.add(workerId)
                ticket._setStatus('pending')
                ticket.worker = null
                ticket._clearTimeout()
                await this._dispatchTicket(ticket, ticket._failedWorkers)
                return
            }

            if (response.type === 'pool:job-error') {
                if (ticket._retriesLeft > 0) {
                    ticket._retriesLeft--
                    ticket._setStatus('pending')
                    ticket.worker = null
                    ticket._clearTimeout()
                    // Don't exclude the worker on error — it may succeed on retry
                    await this._dispatchTicket(ticket)
                    return
                }
                this._emitJobEvent('job:failed', {
                    jobId: ticket.id, jobType: ticket.job.type,
                    worker: workerId, error: response.error,
                })
                ticket._fail(new Error(response.error || 'Worker error'))
                return
            }

            // Success
            if (ticket.status !== 'timed_out' && ticket.status !== 'failed') {
                this._emitJobEvent('job:completed', {
                    jobId: ticket.id, jobType: ticket.job.type,
                    worker: workerId,
                })
                ticket._complete(response.result)

                // Update affinity map
                if (this._dispatchState.affinityMap) {
                    this._dispatchState.affinityMap[ticket.job.type] = workerId
                }
            }
        } catch (err) {
            cleanupProgress()
            if (ticket.status === 'timed_out' || ticket.status === 'failed') return

            if (ticket._retriesLeft > 0) {
                ticket._retriesLeft--
                ticket._setStatus('pending')
                ticket.worker = null
                ticket._clearTimeout()
                await this._dispatchTicket(ticket)
                return
            }

            ticket._fail(err)
        }
    }

    async _dispatchLocal(ticket) {
        ticket._startTimeout(ticket.job.timeout)
        const response = await this._handleJobOffer({
            jobId: ticket.id,
            jobType: ticket.job.type,
            payload: ticket.job.payload,
            timeout: ticket.job.timeout,
        }, null) // null peer — progress ctx handles this

        if (ticket.status === 'timed_out' || ticket.status === 'failed') return

        if (response.type === 'pool:job-reject') {
            ticket._failedWorkers.add(ticket.worker)
            ticket._setStatus('pending')
            ticket.worker = null
            ticket._clearTimeout()
            await this._dispatchTicket(ticket, ticket._failedWorkers)
        } else if (response.type === 'pool:job-error') {
            if (ticket._retriesLeft > 0) {
                ticket._retriesLeft--
                ticket._setStatus('pending')
                ticket.worker = null
                ticket._clearTimeout()
                await this._dispatchTicket(ticket)
            } else {
                ticket._fail(new Error(response.error || 'Worker error'))
            }
        } else {
            ticket._complete(response.result)
        }
    }

    async _getOrConnectPeer(workerId) {
        // Check our cache first
        const cached = this._peerCache.get(workerId)
        if (cached && !cached._destroyed && cached._channel?.readyState === 'open') {
            return cached
        }
        this._peerCache.delete(workerId)

        // Check if there's already a connected peer (e.g., inbound connection)
        const existing = this._group._peers[workerId]
        if (existing && !existing._destroyed && existing._channel?.readyState === 'open') {
            this._peerCache.set(workerId, existing)
            return existing
        }

        const peer = await this._group.connectToPeer(workerId)
        this._peerCache.set(workerId, peer)
        return peer
    }

    _getCapableWorkers(jobType, exclude = null) {
        const capable = new Map()
        for (const [id, info] of this._workers) {
            if (exclude && exclude.has(id)) continue
            if (info.capabilities && info.capabilities.includes(jobType)) {
                capable.set(id, info)
            }
        }
        return capable
    }

    // --- Internal: worker side ---

    _setupWorkerListener() {
        // The Group fires proxiedRequest for messages routed through the
        // discovery server. For direct peer-to-peer job offers, the peer
        // sends a peerProxiedRequest which the Group's sendRequestViaPeer
        // on the submitter side expects a peerProxiedResponse.
        //
        // We hook into the group's connectToPeer mechanism — when a
        // submitter connects and sends a job offer, the peer's dataBig
        // event fires. We need to intercept peerProxiedRequest messages
        // with our pool job type.
        //
        // However, sendRequestViaPeer on the submitter already wraps the
        // request as peerProxiedRequest. On the worker side, we need to
        // listen on each incoming peer for these messages and respond.
        //
        // The simplest integration: override the group's peersList handler
        // to also set up dataBig listeners on incoming peers.
        //
        // For MockGroup testing, we handle this by having the mock's
        // sendRequestViaPeer route through the linked peers' dataBig.

        // Track that we're a worker — peers connecting to us will have
        // their peerProxiedRequest messages handled.
        this._group._workerPool = this
    }

    async _handleJobOffer(data, peer) {
        // Check capacity
        if (this._activeJobs >= this._concurrency) {
            this._rejectedJobs++
            this._updateDeviceInfo(true)
            this._emitJobEvent('job:rejected', {
                jobId: data.jobId, jobType: data.jobType, reason: 'busy',
            })
            return {
                type: 'pool:job-reject',
                jobId: data.jobId,
                reason: 'busy',
            }
        }

        // Check if we handle this type
        const caps = this._group.config?.capabilities || this._options.capabilities || []
        if (!caps.includes(data.jobType)) {
            this._rejectedJobs++
            this._updateDeviceInfo(true)
            this._emitJobEvent('job:rejected', {
                jobId: data.jobId, jobType: data.jobType, reason: 'unknown-type',
            })
            return {
                type: 'pool:job-reject',
                jobId: data.jobId,
                reason: 'unknown-type',
            }
        }

        // Accept and execute
        this._activeJobs++
        this._updateDeviceInfo(true)  // immediate — capacity changed

        const startTime = Date.now()
        this._emitJobEvent('job:started', {
            jobId: data.jobId, jobType: data.jobType,
            worker: this._group.getDeviceInfo?.()?.instanceId || this._group._instanceId,
        })

        const ctx = {
            progress: (progressData) => {
                if (peer && !peer._destroyed) {
                    peer.sendBig({
                        type: 'pool:progress',
                        jobId: data.jobId,
                        data: progressData,
                    })
                } else {
                    // Local dispatch — deliver progress directly to ticket
                    const ticket = this._tickets.get(data.jobId)
                    if (ticket) ticket._progress(progressData)
                }
            },
        }

        try {
            const result = await this._handler(
                { type: data.jobType, payload: data.payload },
                ctx
            )
            const durationMs = Date.now() - startTime
            this._activeJobs--
            this._completedJobs++
            this._recordDuration(durationMs)
            this._recordTypeStats(data.jobType, durationMs, true)
            this._updateDeviceInfo(true)  // immediate — capacity changed
            this._emitJobEvent('job:completed', {
                jobId: data.jobId, jobType: data.jobType, durationMs,
                worker: this._group.getDeviceInfo?.()?.instanceId || this._group._instanceId,
            })
            return {
                type: 'pool:job-result',
                jobId: data.jobId,
                result,
            }
        } catch (err) {
            const durationMs = Date.now() - startTime
            this._activeJobs--
            this._failedJobs++
            this._recordDuration(durationMs)
            this._recordTypeStats(data.jobType, durationMs, false)
            this._updateDeviceInfo(true)  // immediate — capacity changed
            this._emitJobEvent('job:failed', {
                jobId: data.jobId, jobType: data.jobType, durationMs,
                worker: this._group.getDeviceInfo?.()?.instanceId || this._group._instanceId,
                error: err.message,
            })
            return {
                type: 'pool:job-error',
                jobId: data.jobId,
                error: err.message,
            }
        }
    }

    // --- Internal: peer tracking ---

    _onPeersChanged(peers) {
        const myInstanceId = this._group.getDeviceInfo?.()?.instanceId ||
                             this._group._instanceId
        const newWorkers = new Map()
        const newObservers = new Set()

        for (const [id, info] of Object.entries(peers)) {
            if (id === myInstanceId && !this._handler) continue // submitters skip self
            if (info.capabilities && info.capabilities.includes('pool-observer')) {
                newObservers.add(id)
            }
            if (info.capabilities && info.capabilities.length > 0) {
                // Any peer with capabilities is a potential worker
                newWorkers.set(id, info)
            }
        }

        this._observers = newObservers

        // Attach event listeners to peers that may have been created
        // outside of connectToPeer (e.g., by linkGroups in tests)
        if (this._jobEventCallbacks.length > 0 && this._group._peers) {
            for (const peer of this._group._peers.values()) {
                if (!peer._poolEventAttached) {
                    peer._poolEventAttached = true
                    const self = this
                    peer.on('dataBig', (data) => {
                        if (data.type === 'pool:event') {
                            for (const fn of self._jobEventCallbacks) {
                                try { fn(data) } catch (_) {}
                            }
                        }
                    })
                }
            }
        }

        // Detect joins
        for (const [id, info] of newWorkers) {
            if (!this._workers.has(id)) {
                for (const fn of this._workerJoinCallbacks) fn(id, info)
            }
        }

        // Detect leaves
        for (const [id, info] of this._workers) {
            if (!newWorkers.has(id)) {
                this._peerCache.delete(id)
                for (const fn of this._workerLeaveCallbacks) fn(id, info)

                // Check for in-flight jobs assigned to this worker
                for (const [ticketId, ticket] of this._tickets) {
                    if (ticket.worker === id &&
                        ticket.status !== 'completed' &&
                        ticket.status !== 'failed' &&
                        ticket.status !== 'timed_out') {
                        ticket._setStatus('lost')
                        if (ticket._retryOnDisconnect && ticket._retriesLeft > 0) {
                            ticket._retriesLeft--
                            ticket._failedWorkers.add(id)
                            ticket._setStatus('pending')
                            ticket.worker = null
                            ticket._clearTimeout()
                            this._dispatchTicket(ticket, ticket._failedWorkers)
                        } else if (ticket._retryOnDisconnect) {
                            // retryOnDisconnect is true but no retries left —
                            // still try once with remaining workers
                            ticket._failedWorkers.add(id)
                            ticket._setStatus('pending')
                            ticket.worker = null
                            ticket._clearTimeout()
                            this._dispatchTicket(ticket, ticket._failedWorkers)
                        } else {
                            ticket._fail(new Error('Worker disconnected'))
                        }
                    }
                }
            }
        }

        this._workers = newWorkers
    }

    // --- Internal: observability ---

    /**
     * Broadcast a job event to all observer peers.
     * No-op when no observers are present (zero overhead).
     */
    _emitJobEvent(event, details) {
        const eventData = {
            type: 'pool:event',
            event,
            timestamp: Date.now(),
            ...details,
        }

        // Fire local callbacks (for observer-side pools)
        for (const fn of this._jobEventCallbacks) {
            try { fn(eventData) } catch (_) {}
        }

        // Broadcast to remote observer peers
        if (this._observers.size === 0) return
        for (const observerId of this._observers) {
            const peer = this._group.getPeerByInstanceId?.(observerId) ||
                         this._group._peers?.get(observerId)
            if (peer && !peer._destroyed) {
                peer.sendBig(eventData)
            }
        }
    }

    /**
     * Set up listener for incoming pool:event messages (observer side).
     */
    _setupEventListener() {
        if (!this._group.connectToPeer) return

        const self = this
        const attachEventHandler = (peer) => {
            if (peer._poolEventAttached) return
            peer._poolEventAttached = true
            peer.on('dataBig', (data) => {
                if (data.type === 'pool:event') {
                    for (const fn of self._jobEventCallbacks) {
                        try { fn(data) } catch (_) {}
                    }
                }
            })
        }

        // Wrap connectToPeer to attach handler on new connections
        const originalConnect = this._group.connectToPeer.bind(this._group)
        this._group.connectToPeer = async (instanceId) => {
            const peer = await originalConnect(instanceId)
            attachEventHandler(peer)
            return peer
        }

        // Attach to any existing peers (e.g., pre-created by linkGroups)
        if (this._group._peers) {
            for (const peer of this._group._peers.values()) {
                attachEventHandler(peer)
            }
        }
    }

    /**
     * Track per-type stats for a completed or failed job.
     */
    _recordTypeStats(jobType, durationMs, success) {
        if (!this._byType[jobType]) {
            this._byType[jobType] = { completed: 0, failed: 0, durations: [] }
        }
        const stats = this._byType[jobType]
        if (success) stats.completed++
        else stats.failed++
        stats.durations.push(durationMs)
        if (stats.durations.length > this._maxDurations) {
            stats.durations.shift()
        }
    }

    _getByTypeStats() {
        const result = {}
        for (const [type, stats] of Object.entries(this._byType)) {
            const avg = stats.durations.length > 0
                ? Math.round(stats.durations.reduce((a, b) => a + b, 0) / stats.durations.length)
                : 0
            result[type] = {
                completed: stats.completed,
                failed: stats.failed,
                avgMs: avg,
            }
        }
        return result
    }

    // --- Internal: duration tracking ---

    _recordDuration(ms) {
        this._durations.push(ms)
        if (this._durations.length > this._maxDurations) {
            this._durations.shift()
        }
    }

    _getAvgDuration() {
        if (this._durations.length === 0) return 0
        return Math.round(this._durations.reduce((a, b) => a + b, 0) / this._durations.length)
    }

    _getP95Duration() {
        if (this._durations.length === 0) return 0
        const sorted = [...this._durations].sort((a, b) => a - b)
        const idx = Math.ceil(sorted.length * 0.95) - 1
        return sorted[Math.max(0, idx)]
    }

    _updateDeviceInfo(immediate = false) {
        if (!this._group.updateDeviceInfo) return

        const doUpdate = () => {
            this._group.updateDeviceInfo({
                pool: {
                    concurrency: this._concurrency,
                    activeJobs: this._activeJobs,
                    completedJobs: this._completedJobs,
                    failedJobs: this._failedJobs,
                    rejectedJobs: this._rejectedJobs,
                    avgDurationMs: this._getAvgDuration(),
                    p95DurationMs: this._getP95Duration(),
                    startedAt: this._startedAt,
                    byType: this._getByTypeStats(),
                },
            })
        }

        if (immediate) {
            // Clear any pending batched update
            if (this._statUpdateTimer) {
                clearTimeout(this._statUpdateTimer)
                this._statUpdateTimer = null
            }
            doUpdate()
        } else {
            // Batch stat updates
            if (!this._statUpdateTimer) {
                this._statUpdateTimer = setTimeout(() => {
                    this._statUpdateTimer = null
                    doUpdate()
                }, this._statUpdateInterval)
            }
        }
    }
}
